'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/mobile-app/install-and-sign-in.mdx
 * (published at user-docs.totalaccess.co.za/mobile-app/install-and-sign-in)
 */

import { useCallback, useEffect, Suspense, useMemo, useRef, useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { AlertCircle, CheckCircle2, ChevronDown, Download, ExternalLink, Key, Lock, ScanFace, Shield, Smartphone } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '@/hooks/useAuth';
import { appendPostLoginRedirect, getPostLoginRedirect } from '@/lib/authRedirect';
import { buildCentralPasskeyHandoffUrl, buildCentralPasskeyReturnUrl, readCentralPasskeyHandoffStateFromHash, type CentralPasskeyHandoffState } from '@/lib/centralPasskeyFlow';
import { getCentralWebAuthnPortalOrigin, shouldUseCentralWebAuthnFlow } from '@/lib/webauthnRp';
import { isTauriDesktop } from '@/lib/desktop/desktopUtils';
import { capacitorOAuthHandler } from '@/lib/capacitorOAuthHandler';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { buildApiUrl } from '@/lib/platformUtils';
import { frontendLogger } from '@/lib/frontendLoggingService';
import { APPGALLERY_ROUTE, DEFAULT_APP_INSTALL_CTA, PLAYSTORE_ROUTE, resolveAppInstallCta } from '@/lib/appInstallCta';
import { safeIsStandalonePwa } from '@/lib/pwaRuntimeGuards';
import type { User } from '@/lib/authService';
import { authService } from '@/lib/authService';
import ThemeToggle from '@/components/ThemeToggle';
import { LoadingOverlay, SectionLoader, InlineLoadingPill } from '@/components/shared/BrandedLoadingSpinner';
import { LoginIdentifierInput } from '@/components/auth/LoginIdentifierInput';
import FaceAuthenticationModal from '@/components/auth/FaceAuthenticationModal';
import AuthGateModal from '@/components/auth/AuthGateModal';
import { useAuthGate } from '@/hooks/useAuthGate';
import { getCaptchaPassToken } from '@/lib/authGate';
import { getPortalAppHeaders } from '@/lib/pwaAppIdentity';
import { useResolvedAuthPageBranding } from '@/hooks/useAuthPageBranding';
import { navigationService } from '@/lib/navigationService';
import { normalizePhoneNumber } from '@/lib/phoneUtils';
import { validatePassword } from '@/lib/passwordValidation';
import { getLoginLockoutUntil, saveLoginLockout, clearLoginLockout } from '@/lib/loginLockout';
import {
  persistRememberedUsers,
  readRememberedUsers,
  restoreRememberedUsersFromPersistentStorage,
  type RememberedUser,
} from '@/lib/rememberedUsers';
import { tcaSwitchUser } from '@/lib/tcaBridge';
import { useTcaAutoLogin } from '@/hooks/useTcaAutoLogin';
import '../../professional-modern.css';
import 'flag-icons/css/flag-icons.min.css';

/** Check whether an email address belongs to one of the allowed domains */
function isEmailDomainAllowed(email: string, allowedDomains: string[]): boolean {
  if (!allowedDomains.length) return true;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return allowedDomains.some(d => d.toLowerCase() === domain);
}

// The phone-approval QR fallback only renders while a portal-login approval
// is pending — keep qrcode.react out of the login page's base bundle.
const QRCodeSVG = dynamic(
  () => import('qrcode.react').then((m) => m.QRCodeSVG),
  { ssr: false }
);

type LoginMethod = 'password' | 'pin';

const LOGIN_INSTALL_REDIRECT = '/install?install=webapp';
const PUBLIC_PWA_INSTALL_GUIDE_URL = '/resources/downloads/mobile/pwa';

interface PasskeyVerifyUser {
  id?: number | string;
  email?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  has_pin?: boolean;
  role?: string | null;
  is_admin?: boolean | null;
  client_id?: number | string | null;
  employee_id?: number | string | null;
}

interface PasskeyVerifyResponse {
  success?: boolean;
  session_token: string;
  access_token?: string;
  user?: PasskeyVerifyUser;
  error?: string;
  requires_mfa?: boolean;
  mfa_session_token?: string;
  enabled_methods?: string[];
}

interface AuthErrorData {
  error?: string;
  rate_limited?: boolean;
  retryAfter?: number;
  rate_limit_scope?: string;
  requires_captcha?: boolean;
  locked?: boolean;
  locked_until?: string;
  remaining_minutes?: number;
}

interface AuthErrorDetails {
  data?: AuthErrorData;
  message?: string;
  name?: string;
  stack?: string;
  status?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getAuthErrorDetails(error: unknown): AuthErrorDetails {
  if (!isRecord(error)) {
    return {};
  }

  const data = isRecord(error.data)
    ? {
      error: typeof error.data.error === 'string' ? error.data.error : undefined,
      rate_limited: error.data.rate_limited === true ? true : undefined,
      retryAfter: typeof error.data.retryAfter === 'number' ? error.data.retryAfter : undefined,
      rate_limit_scope: typeof error.data.rate_limit_scope === 'string' ? error.data.rate_limit_scope : undefined,
      requires_captcha: error.data.requires_captcha === true ? true : undefined,
      locked: error.data.locked === true ? true : undefined,
      locked_until: typeof error.data.locked_until === 'string' ? error.data.locked_until : undefined,
      remaining_minutes: typeof error.data.remaining_minutes === 'number' ? error.data.remaining_minutes : undefined,
    }
    : undefined;

  return {
    data,
    message: typeof error.message === 'string' ? error.message : undefined,
    name: typeof error.name === 'string' ? error.name : undefined,
    stack: typeof error.stack === 'string' ? error.stack : undefined,
    status: typeof error.status === 'number' ? error.status : undefined,
  };
}

function getErrorMessage(error: unknown): string {
  return getAuthErrorDetails(error).message || '';
}

function getErrorName(error: unknown): string {
  return getAuthErrorDetails(error).name || '';
}

function parseOptionalNumber(value: number | string | null | undefined): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }

  return undefined;
}

function mapPasskeyUserToAuthUser(user: PasskeyVerifyUser | undefined): Partial<User> {
  if (!user) {
    return {};
  }

  return {
    id: parseOptionalNumber(user.id),
    email: user.email || undefined,
    first_name: user.first_name || undefined,
    last_name: user.last_name || undefined,
    avatar_url: user.avatar_url,
    has_pin: user.has_pin,
    role: user.role || undefined,
    is_admin: typeof user.is_admin === 'boolean' ? user.is_admin : undefined,
    client_id: user.client_id || undefined,
    employee_id: parseOptionalNumber(user.employee_id) ?? null,
  };
}

declare global {
  interface Window {
    __coldStartLog?: (tag: string, msg: string, data?: unknown) => void;
    __taStartupScreenReadySignaled?: boolean;
  }
}

function detectLoginRuntime() {
  if (typeof window === 'undefined') {
    return {
      isDesktopApp: false,
      isCapacitorNativeApp: false,
      isStandalonePwaSession: false,
    };
  }

  const desktopApp = isTauriDesktop();
  const capacitorPlatform = Capacitor.getPlatform();
  const capacitorNativeApp = !desktopApp
    && Capacitor.isNativePlatform()
    && capacitorPlatform !== 'web';

  return {
    isDesktopApp: desktopApp,
    isCapacitorNativeApp: capacitorNativeApp,
    isStandalonePwaSession: safeIsStandalonePwa(),
  };
}

function LoginPageContent() {
  const { login, isLoading, isAuthenticated, error: authError, clearError, initiateOAuthFlow, user: authUser } = useAuth();
  const {
    isWhitelabel,
    allowSignup,
    enforceBusinessEmail,
    allowedEmailDomains,
    authBackgroundUrl,
  } = useResolvedAuthPageBranding();
  const searchParams = useSearchParams();
  const router = useRouter();
  const redirectTarget = getPostLoginRedirect(searchParams);
  const redirectParam = getPostLoginRedirect(searchParams, '');
  const initialRuntime = useMemo(() => detectLoginRuntime(), []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDesktopApp, setIsDesktopApp] = useState(initialRuntime.isDesktopApp);
  const [isCapacitorNativeApp, setIsCapacitorNativeApp] = useState(initialRuntime.isCapacitorNativeApp);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const [loginInstallContext, setLoginInstallContext] = useState(DEFAULT_APP_INSTALL_CTA);
  const [isStandalonePwaSession, setIsStandalonePwaSession] = useState(initialRuntime.isStandalonePwaSession);
  const [rememberedEmail, setRememberedEmail] = useState('');
  const [showSwitchUser, setShowSwitchUser] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [pinCode, setPinCode] = useState(['', '', '', '', '', '']);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const [localError, setLocalError] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [routeLoadingState, setRouteLoadingState] = useState<{
    message: string;
    subMessage?: string;
  } | null>(null);
  const loginFormRef = useRef<HTMLFormElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const passkeyHandoffInitializedRef = useRef(false);
  const [passkeyHandoffState, setPasskeyHandoffState] = useState<CentralPasskeyHandoffState | null>(null);
  const lastAutoSubmittedPinRef = useRef<string | null>(null);
  const submitInFlightRef = useRef(false);
  const navFallbackTimerRef = useRef<number | null>(null);

  // Phase 16: TCA auto-login — retrying hook lives below the redirectTarget
  // memo; success handler is wired after authService session storage.

  // Multi-user quick login state
  const [rememberedUsers, setRememberedUsers] = useState<RememberedUser[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RememberedUser | null>(null);

  // SSO accordion state
  const [ssoExpanded, setSsoExpanded] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<Array<{
    id: number;
    protocol: string;
    display_name: string;
    login_url: string | null;
  }>>([]);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoClientId, setSsoClientId] = useState<number | null>(null);
  // SSO profile for returning users — determines which SSO buttons to show
  const [ssoProfile, setSsoProfile] = useState<{
    email: string;
    google_id: string | null;
    google_email: string | null;
    microsoft_id: string | null;
    microsoft_email: string | null;
    intuit_id: string | null;
    intuit_email: string | null;
    xero_id: string | null;
    xero_email: string | null;
    business_email: string | null;
    enforce_business_email: boolean;
  } | null>(null);

  const [pinFailureCount, setPinFailureCount] = useState(0);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ retryAfter: number; scope: string } | null>(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const authGate = useAuthGate(email);
  const rememberedUsersKey = useMemo(
    () => rememberedUsers.map((user) => user.email).sort().join('|'),
    [rememberedUsers]
  );
  const activeRememberedUser = useMemo(() => {
    if (selectedUser?.email) {
      return rememberedUsers.find((user) => user.email === selectedUser.email) || selectedUser;
    }

    if (!rememberedEmail) {
      return null;
    }

    return rememberedUsers.find((user) => user.email === rememberedEmail) || null;
  }, [rememberedEmail, rememberedUsers, selectedUser]);
  // PIN sign-in is server-verified (pin_hash) and safe on every platform —
  // Capacitor is arguably the more trusted device since it is a personal phone.
  const supportsPinLogin = Boolean(
    activeRememberedUser?.hasPin
    && rememberedEmail
    && !showSwitchUser
    && !showUserPicker
    && pinFailureCount < 3,
  );
  // Phase 16: TCA auto-login — resilient retry loop (see useTcaAutoLogin).
  // Retries while the bridge is starting, the screen is locked, or the
  // wrong Windows account is active, and on os_unlocked/os_logon events —
  // so it works when the portal was already open when the session expired,
  // and when the browser loaded before the agent service.
  //
  // Skip after an explicit sign-out or an admin-side session termination:
  // silently re-logging in would defeat both.
  const skipTcaAutoLogin = useMemo(() => {
    const reason = searchParams.get('reason');
    return reason === 'signed_out'
      || reason === 'logout'
      || reason === 'session_terminated'
      || reason === 'account_disabled';
  }, [searchParams]);

  const handleTcaAutoLoginSuccess = useCallback(async (data: {
    user: {
      id: number;
      email: string;
      first_name: string | null;
      last_name: string | null;
      display_name: string | null;
      role: string;
      client_id: number;
      employee_id: number | null;
    };
    tokens: { access_token: string; refresh_token: string; session_token: string };
    session: { id: string; expires_at: string; platform: string };
  }) => {
    const { tokens, user } = data;
    if (tokens.access_token && user) {
      await authService.setAuthenticatedSession(
        {
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token || '',
          session_token: tokens.session_token || '',
        },
        user as Partial<User>
      );
    }

    setRouteLoadingState({
      message: 'Signing you in…',
      subMessage: `Welcome back${user?.first_name ? ', ' + user.first_name : ''}`,
    });

    setTimeout(() => {
      window.location.href = getPostLoginRedirect() || '/client-zone';
    }, 800);
  }, []);

  const tcaAutoLogin = useTcaAutoLogin({
    enabled: !isAuthenticated,
    skip: skipTcaAutoLogin,
    onSuccess: handleTcaAutoLoginSuccess,
  });
  const tcaAutoLoginInProgress = tcaAutoLogin.phase === 'signing_in' || tcaAutoLogin.phase === 'success';

  const showAppLoadingOverlay = Boolean(routeLoadingState) || tcaAutoLoginInProgress;
  const actionIsBusy = isLoading || passkeyLoading || showAppLoadingOverlay || rateLimitCountdown > 0;

  // Identifier (email/phone) validity — disables the Sign In button when invalid
  const identifierValid = useMemo(() => {
    if (!email) return false;
    if (email.includes('@')) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    // SA mobile numbers are 10 digits in national format (0 + 9 digits)
    // or 11 digits with +27 prefix (+27 + 9 digits)
    const digits = email.replace(/\D/g, '');
    if (digits.startsWith('0')) {
      return digits.length === 10;
    }
    if (digits.startsWith('27')) {
      return digits.length === 11;
    }
    return digits.length >= 10;
  }, [email]);

  // Password must satisfy the same creation rules enforced on signup/reset —
  // a password that can't meet them can't be correct, so blocking submit here
  // prevents avoidable failed attempts (and lockouts) and surfaces the
  // requirements to users who are typing the wrong password.
  const passwordValidation = useMemo(() => validatePassword(password), [password]);
  const passwordMeetsRequirements = loginMethod === 'pin' || (password.length > 0 && passwordValidation.isValid);

  // Login identifier normalized the same way as at submit time — used as the
  // per-identifier key for lockout tracking.
  const normalizedIdentifier = useMemo(
    () => normalizePhoneNumber(email).toLowerCase().trim(),
    [email],
  );

  // Account lockout for the identifier currently entered. While active the
  // credential fields are disabled and a live countdown is shown.
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);
  const [showResetHint, setShowResetHint] = useState(false);
  const [showFaceAuth, setShowFaceAuth] = useState(false);
  const [faceModalMode, setFaceModalMode] = useState<'recovery' | 'login'>('recovery');

  // Phone-approved portal sign-in — browser-initiated counterpart of the
  // §30 Windows face-logon flow. A nonce pushes to the user's enrolled
  // installs; this browser polls until the phone approves/denies/expiry.
  const [phoneApproval, setPhoneApproval] = useState<{
    nonce: string;
    qrLink: string;
    expiresAt: number;
  } | null>(null);
  const [phoneApprovalPending, setPhoneApprovalPending] = useState(false);
  const [phoneApprovalError, setPhoneApprovalError] = useState<string | null>(null);
  const phoneApprovalPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLockedOut = lockoutUntil !== null && lockoutSecondsLeft > 0;
  const lockoutCountdownLabel = `${Math.floor(lockoutSecondsLeft / 60)}:${String(lockoutSecondsLeft % 60).padStart(2, '0')}`;

  // Face-verified recovery is a Capacitor-only path — it needs the native
  // device_id/app_id binding plus the on-device camera. Offered only when
  // credentials are failing or locked; the server still enforces that the
  // device is known and the user has an enrolled face template.
  const faceRecoveryOffered = isCapacitorNativeApp && identifierValid && (isLockedOut || showResetHint);

  // Face login is offered on any Capacitor device with a valid identifier —
  // the server decides eligibility (known device + enrolled template), and
  // downstream MFA/device-verification policy still applies after the token
  // is redeemed through the normal /auth/login pipeline.
  const faceLoginOffered = isCapacitorNativeApp && identifierValid && !isLockedOut;

  // Re-check the stored lockout whenever the entered identifier changes —
  // switching to a different account re-enables the form.
  useEffect(() => {
    const until = getLoginLockoutUntil(normalizedIdentifier);
    setLockoutUntil(until !== null && until > Date.now() ? until : null);
  }, [normalizedIdentifier]);

  // Live countdown — clears the stored lockout (and any stale error) at zero.
  useEffect(() => {
    if (lockoutUntil === null) {
      setLockoutSecondsLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
      setLockoutSecondsLeft(remaining);
      if (remaining <= 0) {
        clearLoginLockout(normalizedIdentifier);
        setLockoutUntil(null);
        setLocalError(null);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil, normalizedIdentifier]);

  // Whether the identifier input is currently visible (not a remembered user)
  const isIdentifierInputVisible = (!rememberedEmail && !showUserPicker) || showSwitchUser;

  // Phone-approved portal sign-in is offered only in the welcome-back state:
  // the account is already known (remembered user), so a push can be routed
  // to the user's enrolled installs. Capacitor gets the direct face-login
  // button instead — pushing to the same device the user is holding makes
  // no sense. Server still decides eligibility and rate-limits.
  const phoneApprovalOffered = !isCapacitorNativeApp
    && Boolean(rememberedEmail)
    && !isIdentifierInputVisible
    && !isLockedOut;

  // Focus the password field when the user presses Enter in the identifier input.
  const focusPassword = useCallback(() => {
    passwordInputRef.current?.focus();
  }, []);

  // PIN failure tracking helpers
  const getPinFailureCount = useCallback((userEmail: string): number => {
    try {
      const data = JSON.parse(localStorage.getItem('pin_login_failures') || '{}');
      return data[userEmail] || 0;
    } catch {
      return 0;
    }
  }, []);

  const savePinFailureCount = useCallback((userEmail: string, count: number) => {
    try {
      const data = JSON.parse(localStorage.getItem('pin_login_failures') || '{}');
      data[userEmail] = count;
      localStorage.setItem('pin_login_failures', JSON.stringify(data));
    } catch {
      // ignore
    }
  }, []);

  const startRouteLoading = useCallback((message: string, subMessage?: string) => {
    setRouteLoadingState((current) => current ?? { message, subMessage });
  }, []);

  // Build a personalised welcome message for the post-login loading spinner.
  // Falls back to a generic message when the user's name isn't available.
  const buildWelcomeMessage = useCallback(
    (firstName?: string | null, lastName?: string | null, fallback = 'Signing you in...') => {
      const full = `${firstName || ''} ${lastName || ''}`.trim();
      return full ? `Welcome, ${full}` : fallback;
    },
    [],
  );

  // Hard-navigation fallback: if a client-side route change stalls (stale
  // build chunks, hung RSC fetch) the loading overlay would otherwise spin
  // forever. The pathname check makes this a no-op when the SPA nav landed,
  // and the timer is cleared when this component unmounts on a successful nav.
  const armNavFallback = useCallback((href: string) => {
    if (navFallbackTimerRef.current) {
      window.clearTimeout(navFallbackTimerRef.current);
    }
    navFallbackTimerRef.current = window.setTimeout(() => {
      const current = `${window.location.pathname}${window.location.search}`;
      if (current !== href) {
        window.location.assign(href);
      }
    }, 15000);
  }, []);

  useEffect(() => () => {
    if (navFallbackTimerRef.current) {
      window.clearTimeout(navFallbackTimerRef.current);
    }
  }, []);

  const pushWithLoading = useCallback((href: string, message: string, subMessage?: string) => {
    startRouteLoading(message, subMessage);
    router.push(href);
    armNavFallback(href);
  }, [router, startRouteLoading, armNavFallback]);

  const replaceWithLoading = useCallback((href: string, message: string, subMessage?: string) => {
    startRouteLoading(message, subMessage);
    router.replace(href);
    armNavFallback(href);
  }, [router, startRouteLoading, armNavFallback]);

  const createLoadingLinkHandler = useCallback((href: string, message: string, subMessage?: string) => {
    return (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      pushWithLoading(href, message, subMessage);
    };
  }, [pushWithLoading]);

  const primeOfflinePortalShell = useCallback(async () => {
    try {
      await navigationService.primeOfflineNavigationSnapshot({ force: true });
    } catch (error) {
      console.warn('[LOGIN] Failed to prime offline navigation snapshot:', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || window.__taStartupScreenReadySignaled) {
      return;
    }

    const timer = setTimeout(() => {
      window.__coldStartLog?.('APP_READY_SIGNAL', 'Login page dispatching ready signal', {
        path: window.location.pathname,
        href: window.location.href,
        perfNow: Math.round(performance.now()),
      });
      window.__taStartupScreenReadySignaled = true;
      window.dispatchEvent(new CustomEvent('ta-app-screen-ready'));
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Check if we're in a hang recovery flow — if so, clear auth state
    // and show the login form instead of redirecting back to client-zone
    // (which would re-trigger the hang).
    try {
      const hangRecovery = window.sessionStorage.getItem('_ta_hang_recovery');
      if (hangRecovery === '1') {
        console.warn('[LOGIN] Hang recovery detected — clearing auth state and showing login form');
        window.sessionStorage.removeItem('_ta_hang_recovery');
        authService.clearAuth();
        return;
      }
    } catch(e) {}

    startRouteLoading(
      buildWelcomeMessage(authUser?.first_name, authUser?.last_name, 'Completing sign-in...'),
      'Loading your workspace and redirecting you now.',
    );

    const redirectTimer = window.setTimeout(() => {
      router.replace(redirectTarget);
    }, 50);

    // Safety net: if the client-side navigation never completes (stale build
    // chunks, hung RSC fetch), fall back to a full document navigation so the
    // browser fetches fresh HTML + chunks directly from the network instead of
    // leaving the user on the "Loading your workspace" spinner indefinitely.
    const hardNavTimer = window.setTimeout(() => {
      window.location.assign(redirectTarget);
    }, 30000);

    return () => {
      window.clearTimeout(redirectTimer);
      window.clearTimeout(hardNavTimer);
    };
  }, [isAuthenticated, redirectTarget, router, startRouteLoading, authUser, buildWelcomeMessage]);

  useEffect(() => {
    const recovery = searchParams.get('recovery');
    const reason = searchParams.get('reason');

    if (recovery !== '1') {
      return;
    }

    setSuccessMessage('Recovery mode is active. Please sign in again to continue.');
  }, [searchParams]);

  // Display OAuth error messages from redirect (e.g. Intuit account not linked)
  useEffect(() => {
    const oauthError = searchParams.get('error');
    const oauthMessage = searchParams.get('message');
    const oauthProvider = searchParams.get('provider');

    if (oauthError === 'oauth_failed' && oauthMessage) {
      const providerName = oauthProvider ? oauthProvider.charAt(0).toUpperCase() + oauthProvider.slice(1) : 'OAuth';
      setLocalError(`${providerName}: ${oauthMessage}`);
    }
  }, [searchParams]);

  // Combined error from auth hook and local state
  const error = localError || authError;
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const centralPasskeyOrigin = useMemo(
    () => (currentHostname ? getCentralWebAuthnPortalOrigin(currentHostname) : ''),
    [currentHostname]
  );
  const shouldUseCentralPasskeyLogin = useMemo(
    () => Boolean(!isCapacitorNativeApp && currentHostname && shouldUseCentralWebAuthnFlow(currentHostname)),
    [currentHostname, isCapacitorNativeApp]
  );
  const centralPasskeyHostLabel = useMemo(() => {
    if (!centralPasskeyOrigin) {
      return 'central sign-in';
    }

    try {
      return new URL(centralPasskeyOrigin).host;
    } catch {
      return centralPasskeyOrigin;
    }
  }, [centralPasskeyOrigin]);
  const installAfterLoginHref = useMemo(
    () => appendPostLoginRedirect('/auth/login', LOGIN_INSTALL_REDIRECT),
    [],
  );
  const isInstallRedirectArmed = useMemo(
    () => redirectTarget === LOGIN_INSTALL_REDIRECT || redirectParam === LOGIN_INSTALL_REDIRECT,
    [redirectParam, redirectTarget],
  );
  const showLoginInstallCard = useMemo(
    () => !isDesktopApp
      && !isCapacitorNativeApp
      && !isStandalonePwaSession
      && loginInstallContext.mode !== 'mobile-device-required',
    [isCapacitorNativeApp, isDesktopApp, isStandalonePwaSession, loginInstallContext.mode],
  );
  const loginInstallCardTitle = loginInstallContext.mode === 'android-store'
    ? 'Install the Android app on this phone'
    : loginInstallContext.mode === 'appgallery'
      ? 'Install the app from AppGallery on this phone'
      : 'Install the TotalAccess web app on this phone';
  const loginInstallCardBody = (loginInstallContext.mode === 'android-store' || loginInstallContext.mode === 'appgallery')
    ? loginInstallContext.description
    : 'Sign in first, then continue to the guided browser-install flow for this device.';
  const loginInstallGuideLabel = (loginInstallContext.mode === 'android-store' || loginInstallContext.mode === 'appgallery')
    ? 'Need the browser-install route instead?'
    : 'View the install guide';

  useEffect(() => {
    if (passkeyHandoffInitializedRef.current || typeof window === 'undefined') {
      return;
    }

    passkeyHandoffInitializedRef.current = true;

    const nextState = readCentralPasskeyHandoffStateFromHash(window.location.hash, 'passkey_login_handoff_state');

    if (!nextState) {
      return;
    }

    try {
      setPasskeyHandoffState(nextState);

      if (nextState.email) {
        setEmail(nextState.email);
      }

      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
      setSuccessMessage(`Continue your secure passkey sign-in here, then we’ll send you back to ${nextState.sourceHostname || new URL(nextState.returnOrigin).host}.`);
    } catch (handoffError) {
      console.error('[LOGIN] Failed to restore passkey handoff state:', handoffError);
      setLocalError('We could not restore your secure passkey handoff. Please return to your sign-in page and try again.');
    }
  }, []);

  const finishPasskeyLogin = useCallback(async (verifyData: PasskeyVerifyResponse) => {
    const returnOrigin = passkeyHandoffState?.returnOrigin;
    const returnRedirectParam = passkeyHandoffState?.redirectParam || redirectParam;

    if (returnOrigin && typeof window !== 'undefined') {
      try {
        const returnUrlString = buildCentralPasskeyReturnUrl({
          returnOrigin,
          redirectParam: returnRedirectParam,
          verifyData,
        });

        if (returnUrlString) {
          const returnUrl = new URL(returnUrlString);

          if (returnUrl.origin !== window.location.origin) {
            window.location.href = returnUrl.toString();
            return;
          }
        }
      } catch (redirectError) {
        console.error('[LOGIN] Failed to build passkey return redirect:', redirectError);
      }
    }

    const { authService } = await import('@/lib/authService');

    console.log('[LOGIN] Calling authService.setPasskeyAuth...');
    await authService.setPasskeyAuth(
      verifyData.session_token,
      mapPasskeyUserToAuthUser(verifyData.user),
      verifyData.access_token,
    );

    console.log('[LOGIN] Passkey auth state set successfully');
    await new Promise(resolve => setTimeout(resolve, 200));
    void primeOfflinePortalShell();

    setTimeout(() => {
      console.log('[LOGIN] Executing redirect...');
      replaceWithLoading(
        redirectTarget,
        buildWelcomeMessage(verifyData.user?.first_name, verifyData.user?.last_name, 'Signing you in...'),
        'Opening your workspace now.',
      );
    }, 300);
  }, [passkeyHandoffState, primeOfflinePortalShell, redirectParam, redirectTarget, replaceWithLoading, buildWelcomeMessage]);

  const continuePasskeyOnCentralPortal = useCallback((mode: 'discoverable' | 'email') => {
    try {
      setLocalError(null);

      if (!centralPasskeyOrigin) {
        throw new Error('No shared passkey portal is configured for this environment.');
      }

      if (mode === 'email' && !email) {
        throw new Error('Please enter your email address or phone number first');
      }

      const handoffState: CentralPasskeyHandoffState = {
        returnOrigin: currentOrigin,
        redirectParam,
        email: mode === 'email' ? email : undefined,
        sourceHostname: currentHostname,
      };

      window.location.href = buildCentralPasskeyHandoffUrl({
        centralOrigin: centralPasskeyOrigin,
        path: '/auth/login',
        hashParamName: 'passkey_login_handoff_state',
        state: handoffState,
        redirectParam,
        extraSearchParams: {
          passkey_handoff: '1',
          passkey_mode: mode,
          login_hint: mode === 'email' ? email : undefined,
        },
      });
    } catch (handoffError: unknown) {
      console.error('[LOGIN] Failed to start central passkey login handoff:', handoffError);
      setLocalError(getErrorMessage(handoffError) || 'Failed to continue with secure central passkey sign-in.');
    }
  }, [centralPasskeyOrigin, currentHostname, currentOrigin, email, redirectParam]);

  const loadRememberedUsers = useCallback((): RememberedUser[] => {
    return readRememberedUsers();
  }, []);

  const loadRememberedUser = useCallback((runtime?: { isDesktopApp?: boolean; isCapacitorNativeApp?: boolean }) => {
    try {
      const users = loadRememberedUsers();
      setRememberedUsers(users);

      // Determine if we're in desktop/mobile mode
      const isSharedDeviceMode = Boolean(runtime?.isDesktopApp || runtime?.isCapacitorNativeApp);

      // For desktop/Tauri/Capacitor, show user picker if multiple users
      if (isSharedDeviceMode && users.length > 1) {
        setShowUserPicker(true);
        setRememberedEmail('');
      } else if (users.length > 0) {
        // Single user or web - use most recent
        const mostRecent = users[0];
        setRememberedEmail(mostRecent.email);
        setEmail(mostRecent.email);
        setSelectedUser(mostRecent);
        setPinCode(['', '', '', '', '', '']);
        setLoginMethod(mostRecent.hasPin ? 'pin' : 'password');

        // Load remembered password if available
        if (mostRecent.hasStoredPassword) {
          const savedPassword = localStorage.getItem('remembered_password');
          if (savedPassword) {
            const passwordData = JSON.parse(savedPassword);
            if (passwordData.email === mostRecent.email && passwordData.password) {
              setPassword(passwordData.password);
              setRememberPassword(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('[LOGIN] Error loading remembered user:', error);
      localStorage.removeItem('remembered_user');
      localStorage.removeItem('remembered_users');
      localStorage.removeItem('remembered_password');
    }
  }, [loadRememberedUsers]);

  // Check if running in desktop app or Capacitor
  useEffect(() => {
    const runtime = detectLoginRuntime();
    const syncInstallSurface = () => {
      const nextRuntime = detectLoginRuntime();
      setIsDesktopApp(nextRuntime.isDesktopApp);
      setIsCapacitorNativeApp(nextRuntime.isCapacitorNativeApp);
      setLoginInstallContext(resolveAppInstallCta(window.navigator));
      setIsStandalonePwaSession(nextRuntime.isStandalonePwaSession);
    };

    syncInstallSurface();
    window.addEventListener('appinstalled', syncInstallSurface);

    // Load remembered users with platform info. On Capacitor, first restore
    // the list from the Preferences mirror when localStorage was wiped
    // (startup watchdog clear, OS reclaim of WebView storage).
    void (async () => {
      if (runtime.isCapacitorNativeApp) {
        await restoreRememberedUsersFromPersistentStorage();
      }
      loadRememberedUser({
        isDesktopApp: runtime.isDesktopApp,
        isCapacitorNativeApp: runtime.isCapacitorNativeApp,
      });
    })();

    let cleanupCapacitorOAuth: (() => void) | undefined;

    // Start listening for OAuth callbacks in Capacitor
    if (runtime.isCapacitorNativeApp) {
      console.log('[LOGIN] Starting Capacitor OAuth listener');
      capacitorOAuthHandler.startListening();

      cleanupCapacitorOAuth = () => {
        console.log('[LOGIN] Cleaning up Capacitor OAuth listener');
        capacitorOAuthHandler.stopListening();
      };
    }

    return () => {
      window.removeEventListener('appinstalled', syncInstallSurface);
      cleanupCapacitorOAuth?.();
    };
  }, [loadRememberedUser]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    const updateDesktopViewport = () => setIsDesktopViewport(desktopMediaQuery.matches);
    updateDesktopViewport();

    if (typeof desktopMediaQuery.addEventListener === 'function') {
      desktopMediaQuery.addEventListener('change', updateDesktopViewport);
      return () => desktopMediaQuery.removeEventListener('change', updateDesktopViewport);
    }

    desktopMediaQuery.addListener(updateDesktopViewport);
    return () => desktopMediaQuery.removeListener(updateDesktopViewport);
  }, []);

  useEffect(() => {
    if (!rememberedUsersKey || typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    const refreshRememberedUserPinStatus = async () => {
      try {
        const logins = rememberedUsersKey.split('|').filter(Boolean);
        if (!logins.length) {
          return;
        }

        const response = await fetch('/api/v1/auth/pin/lookup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ logins }),
        });

        if (!response.ok) {
          return;
        }

        const result = await response.json();
        if (!result.success || !result.statuses || cancelled) {
          return;
        }

        setRememberedUsers((previousUsers) => {
          let changed = false;
          const nextUsers = previousUsers.map((user) => {
            // The API keys statuses by the lower-cased login — stored
            // identifiers (esp. whitelabel business emails) may contain
            // capitals, so look up the normalized form first.
            const status = result.statuses?.[user.email.trim().toLowerCase()]
              ?? result.statuses?.[user.email];

            // A missing status means the identifier couldn't be resolved —
            // keep the stored flag instead of wrongly revoking PIN support.
            if (!status) {
              return user;
            }

            const hasPin = Boolean(status.hasPin);
            if (user.hasPin === hasPin) {
              return user;
            }

            changed = true;
            return { ...user, hasPin };
          });

          if (changed) {
            persistRememberedUsers(nextUsers);
            return nextUsers;
          }

          return previousUsers;
        });
      } catch (refreshError) {
        console.warn('[LOGIN] Failed to refresh remembered PIN status:', refreshError);
      }
    };

    refreshRememberedUserPinStatus();

    return () => {
      cancelled = true;
    };
  }, [rememberedUsersKey]);

  useEffect(() => {
    // Only force the switch while the PIN boxes are untouched — an async
    // pin/lookup refresh must not wipe digits the user is mid-way through.
    if (loginMethod === 'pin' && !supportsPinLogin && pinCode.every((digit) => !digit)) {
      setLoginMethod('password');
      setPinCode(['', '', '', '', '', '']);
    }
  }, [loginMethod, supportsPinLogin, pinCode]);

  useEffect(() => {
    if (loginMethod !== 'pin') {
      lastAutoSubmittedPinRef.current = null;
      return;
    }

    if (pinCode.some((digit) => !digit)) {
      lastAutoSubmittedPinRef.current = null;
    }
  }, [loginMethod, pinCode]);

  useEffect(() => {
    if (loginMethod !== 'pin' || !supportsPinLogin) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      pinInputRefs.current[0]?.focus();
    }, 100);

    return () => window.clearTimeout(focusTimer);
  }, [loginMethod, supportsPinLogin]);

  // Sync password-manager autofill that fills the input without firing events
  // React observes (field mounted after page load, silent extension fill).
  // Polls briefly after the password field mounts; clears itself once the
  // input has a value and never overwrites state the user already typed into.
  useEffect(() => {
    if (loginMethod !== 'password') {
      return;
    }

    const interval = window.setInterval(() => {
      const input = passwordInputRef.current;
      if (!input?.value) {
        return;
      }
      window.clearInterval(interval);
      setPassword((current) => current || input.value);
    }, 300);

    const timeout = window.setTimeout(() => window.clearInterval(interval), 5000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [loginMethod]);

  // Sync PIN failure count from localStorage when remembered user changes
  useEffect(() => {
    if (rememberedEmail) {
      setPinFailureCount(getPinFailureCount(rememberedEmail));
    } else {
      setPinFailureCount(0);
    }
  }, [rememberedEmail, getPinFailureCount]);

  // Handle selecting a user from the picker
  const handleSelectUser = (user: RememberedUser) => {
    setSelectedUser(user);
    setRememberedEmail(user.email);
    setEmail(user.email);
    setShowUserPicker(false);
    setShowSwitchUser(false);
    setPinCode(['', '', '', '', '', '']);
    setLoginMethod(user.hasPin ? 'pin' : 'password');
    setPassword('');
    setRememberPassword(false);
    setPinFailureCount(0);
    setSsoProfile(null);
    if (user.email) {
      savePinFailureCount(user.email, 0);
    }

    // Load stored password if available
    if (user.hasStoredPassword) {
      try {
        const savedPassword = localStorage.getItem('remembered_password');
        if (savedPassword) {
          const passwordData = JSON.parse(savedPassword);
          if (passwordData.email === user.email && passwordData.password) {
            setPassword(passwordData.password);
            setRememberPassword(true);
          }
        }
      } catch (e) {
        console.error('[LOGIN] Error loading password for user:', e);
      }
    }
  };

  // Fetch SSO profile for a returning user so we can show only the SSO
  // buttons they have linked, and pass their email as login_hint.
  useEffect(() => {
    if (!selectedUser?.userId) {
      setSsoProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/v1/auth/sso-profile?user_id=${selectedUser.userId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.success && data.profile && data.profile.userId > 0) {
          setSsoProfile(data.profile);
        }
      } catch {
        // Non-fatal — SSO buttons just won't be filtered
      }
    })();
    return () => { cancelled = true; };
  }, [selectedUser]);

  // Forget a specific user from remembered list
  const handleForgetUser = (userEmail: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    try {
      const users = rememberedUsers.filter(u => u.email !== userEmail);
      setRememberedUsers(users);
      persistRememberedUsers(users);

      // Also remove password if it was for this user
      const savedPassword = localStorage.getItem('remembered_password');
      if (savedPassword) {
        const passwordData = JSON.parse(savedPassword);
        if (passwordData.email === userEmail) {
          localStorage.removeItem('remembered_password');
        }
      }

      // Clear PIN failure count for this user
      savePinFailureCount(userEmail, 0);

      // If we just removed the selected user, reset
      if (selectedUser?.email === userEmail || rememberedEmail === userEmail) {
        setSelectedUser(null);
        setRememberedEmail('');
        setEmail('');
        setPassword('');
        setPinFailureCount(0);

        if (users.length > 0) {
          handleSelectUser(users[0]);
        }
      }

      console.log('[LOGIN] Forgot user:', userEmail);
    } catch (error) {
      console.error('[LOGIN] Error forgetting user:', error);
    }
  };

  // Handle switch user
  const handleSwitchUser = () => {
    // On desktop with multiple users, show the picker
    if ((isDesktopApp || isCapacitorNativeApp) && rememberedUsers.length > 1) {
      setShowUserPicker(true);
      setRememberedEmail('');
    } else {
      setShowSwitchUser(true);
    }
    setEmail('');
    setPassword('');
    setPinCode(['', '', '', '', '', '']);
    setLoginMethod('password');
    setRememberPassword(false);
    setPinFailureCount(0);
    setSelectedUser(null);
    setSsoProfile(null);
    if (rememberedEmail) {
      savePinFailureCount(rememberedEmail, 0);
    }
  };

  // Save credentials (updated for multi-user)
  const saveCredentials = (userInfo?: { userId?: number; displayName?: string; avatarUrl?: string; hasPin?: boolean }) => {
    try {
      const normalizedLogin = normalizePhoneNumber(email);
      const users = loadRememberedUsers();

      // Find or create user entry
      const existingIndex = users.findIndex(u =>
        normalizePhoneNumber(u.email) === normalizedLogin || u.email === email
      );
      const existingUser = existingIndex >= 0 ? users[existingIndex] : undefined;
      const shouldPersistStoredPassword = loginMethod === 'password'
        ? rememberPassword && !!password
        : Boolean(existingUser?.hasStoredPassword);

      const newUser: RememberedUser = {
        email: normalizedLogin,
        userId: userInfo?.userId ?? existingUser?.userId,
        displayName: userInfo?.displayName ?? existingUser?.displayName,
        avatarUrl: userInfo?.avatarUrl ?? existingUser?.avatarUrl,
        lastLogin: Date.now(),
        hasStoredPassword: shouldPersistStoredPassword,
        hasPin: userInfo?.hasPin ?? existingUser?.hasPin
      };

      if (existingIndex >= 0) {
        // Update existing
        users[existingIndex] = { ...users[existingIndex], ...newUser };
      } else {
        // Add new user (keep max 10 users)
        users.unshift(newUser);
        if (users.length > 10) {
          users.pop();
        }
      }

      // Persist the list — on Capacitor this also mirrors to Preferences so
      // the remembered user survives WebView storage wipes.
      persistRememberedUsers(users);

      // Also keep legacy format for backwards compatibility
      localStorage.setItem('remembered_user', JSON.stringify({
        email: normalizedLogin,
        timestamp: Date.now()
      }));

      // Save password if checkbox is checked
      if (loginMethod === 'password' && rememberPassword && password) {
        localStorage.setItem('remembered_password', JSON.stringify({
          email: normalizedLogin,
          password,
          savedAt: new Date().toISOString()
        }));
      } else if (loginMethod === 'password' && !rememberPassword) {
        // Only remove if unchecked, not if password is empty (might be passkey login)
        const savedPassword = localStorage.getItem('remembered_password');
        if (savedPassword) {
          const passwordData = JSON.parse(savedPassword);
          if (passwordData.email === normalizedLogin) {
            localStorage.removeItem('remembered_password');
          }
        }
      }
    } catch (error) {
      console.error('[LOGIN] Error saving credentials:', error);
    }
  };

  // Check for success messages from URL parameters
  useEffect(() => {
    const resetSuccess = searchParams.get('reset');
    const signupSuccess = searchParams.get('signup');
    const verified = searchParams.get('verified');

    if (resetSuccess === 'success') {
      // Only promise the MFA step when the reset actually determined the
      // account will require it — most password-only users sign straight in.
      const mfaRequired = searchParams.get('mfa') === 'required';
      setSuccessMessage(mfaRequired
        ? 'Password reset successful! Please sign in with your new password. For security, you will need to complete multi-factor authentication.'
        : 'Password reset successful! Please sign in with your new password.');
    } else if (signupSuccess === 'success') {
      setSuccessMessage('Account created successfully! Please sign in.');
    } else if (verified === 'true') {
      setSuccessMessage('Account verified successfully! Please sign in.');
    }
  }, [searchParams]);

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitCountdown <= 0) {
      setRateLimitInfo(null);
      return;
    }
    const interval = setInterval(() => {
      setRateLimitCountdown(prev => {
        if (prev <= 1) {
          setRateLimitInfo(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [rateLimitCountdown]);

  useEffect(() => {
    const loginHint = (
      searchParams.get('login_hint')
      || searchParams.get('email')
      || searchParams.get('phone')
      || ''
    ).trim();

    if (!loginHint) {
      return;
    }

    const normalizedHint = loginHint.includes('@') ? loginHint : normalizePhoneNumber(loginHint);

    setEmail(normalizedHint);
    setRememberedEmail('');
    setSelectedUser(null);
    setShowUserPicker(false);
    setShowSwitchUser(true);
    setPassword('');
    setPinCode(['', '', '', '', '', '']);
    setLoginMethod('password');
    setRememberPassword(false);
    setLocalError(null);

    setSuccessMessage((current) => current || 'We pre-filled your sign-in details. Enter your password to continue on this device.');
  }, [searchParams]);

  const handlePasskeySignIn = async () => {
    if (!email) {
      setLocalError('Please enter your email address or phone number first');
      return;
    }

    try {
      setLocalError(null);
      setPasskeyLoading(true);

      // Prefer the stored user ID for returning users (direct lookup).
      // Fall back to normalised email/phone for new users.
      const rememberedUser = rememberedUsers.find(
        (u) => normalizePhoneNumber(u.email) === normalizePhoneNumber(email)
      );
      const userId = rememberedUser?.userId;
      const normalizedIdentifier = email.includes('@') ? email : normalizePhoneNumber(email);

      // Get authentication options from server
      const optionsResponse = await fetch('/api/v1/auth/webauthn/authenticate/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify(userId ? { userId } : { email: normalizedIdentifier })
      });

      const optionsData = await optionsResponse.json();
      if (!optionsResponse.ok) {
        throw new Error(optionsData.error || 'Failed to get authentication options');
      }

      // Start WebAuthn authentication ceremony
      const authResponse = await startAuthentication(optionsData.options);

      // Verify authentication with server - include the original challenge
      const verifyResponse = await fetch('/api/v1/auth/webauthn/authenticate/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify({
          userId: optionsData.userId,
          response: authResponse,
          challenge: optionsData.options.challenge // Pass the original challenge
        })
      });

      const verifyData: PasskeyVerifyResponse = await verifyResponse.json();
      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Passkey verification failed');
      }

      if (verifyData.success) {
        // Check if MFA is required
        if (verifyData.requires_mfa && verifyData.mfa_session_token) {
          console.log('[LOGIN] Passkey successful but MFA required, redirecting to MFA page');

          // Store MFA context for the MFA page
          const mfaContext = {
            userId: verifyData.user?.id,
            email: verifyData.user?.email,
            sessionToken: verifyData.mfa_session_token,
            mfaMethods: Array.isArray(verifyData.enabled_methods) && verifyData.enabled_methods.length > 0
              ? verifyData.enabled_methods
              : ['webauthn', 'inapp_approval', 'totp', 'email_otp', 'whatsapp_otp']
          };

          document.cookie = `mfa_pending_context=${encodeURIComponent(JSON.stringify(mfaContext))}; path=/; max-age=900; SameSite=Lax`;

          // Redirect to MFA page
          setTimeout(() => {
            router.push('/auth/mfa');
          }, 100);
          return;
        }

        // Save email for future use
        try {
          localStorage.setItem('remembered_user', JSON.stringify({
            email,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.error('Failed to save email:', e);
        }

        console.log('[LOGIN] Passkey authentication successful!', {
          hasSessionToken: !!verifyData.session_token,
          hasUser: !!verifyData.user,
          userId: verifyData.user?.id
        });

        setSuccessMessage('Passkey authentication successful! Redirecting...');

        // Save user to remembered users list
        saveCredentials({
          userId: parseOptionalNumber(verifyData.user?.id),
          displayName: verifyData.user?.first_name
            ? `${verifyData.user.first_name} ${verifyData.user.last_name || ''}`.trim()
            : undefined,
          avatarUrl: verifyData.user?.avatar_url,
          hasPin: verifyData.user?.has_pin
        });

        if (verifyData.session_token && verifyData.user) {
          await finishPasskeyLogin(verifyData);
        } else {
          console.error('[LOGIN] Missing session_token or user data:', {
            hasToken: !!verifyData.session_token,
            hasUser: !!verifyData.user
          });
        }
      }
    } catch (err: unknown) {
      console.error('[LOGIN] Passkey error:', err);

      // Handle specific error messages from server
      const errorMessage = getErrorMessage(err);
      const errorName = getErrorName(err);

      if (errorMessage.includes('No passkeys registered')) {
        setLocalError('No passkeys found for this account. Please sign in with your password or register a passkey first.');
      } else if (errorMessage.includes('User not found')) {
        setLocalError('No account found with this email address. Please check your email or sign up.');
      } else if (errorMessage.includes('Passkey verification failed')) {
        setLocalError('Passkey verification failed. This could mean an incorrect PIN or password was entered. Please try again.');
      } else if (errorName === 'NotAllowedError') {
        // NotAllowedError typically means incorrect PIN/password, timeout, or user cancelled
        setLocalError('Authentication failed. This could be due to an incorrect PIN/password, timeout, or cancellation. Please try again or use your regular password.');
      } else if (errorName === 'InvalidStateError') {
        setLocalError('Invalid authentication state. Please refresh the page and try again.');
      } else if (errorName === 'AbortError') {
        setLocalError('Passkey authentication was cancelled. Please try again.');
      } else if (errorName === 'SecurityError') {
        setLocalError('Security error during passkey authentication. Please ensure you are on a secure connection and try again.');
      } else if (errorName === 'NotSupportedError') {
        setLocalError('Your browser or device does not support passkeys. Please sign in with your password.');
      } else {
        setLocalError('Passkey authentication failed. Please try again or sign in with your password.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleDiscoverablePasskeyLogin = async () => {
    try {
      setLocalError(null);
      setPasskeyLoading(true);

      // Step 1: Get authentication options (no email required)
      const optionsResponse = await fetch('/api/v1/auth/webauthn/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify({})
      });

      const optionsData = await optionsResponse.json();
      if (!optionsResponse.ok) {
        throw new Error(optionsData.error || 'Failed to get authentication options');
      }

      // Step 2: Start WebAuthn authentication ceremony with discoverable credentials
      const authResponse = await startAuthentication(optionsData.options);

      // Step 3: Verify authentication with server
      const verifyResponse = await fetch('/api/v1/auth/webauthn/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify({
          response: authResponse,
          challenge: optionsData.challenge
        })
      });

      const verifyData: PasskeyVerifyResponse = await verifyResponse.json();
      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Passkey verification failed');
      }

      if (verifyData.success) {
        // Check if MFA is required
        if (verifyData.requires_mfa && verifyData.mfa_session_token) {
          console.log('[LOGIN] Discoverable passkey successful but MFA required, redirecting to MFA page');

          // Store MFA context for the MFA page
          const mfaContext = {
            userId: verifyData.user?.id,
            email: verifyData.user?.email,
            sessionToken: verifyData.mfa_session_token,
            mfaMethods: Array.isArray(verifyData.enabled_methods) && verifyData.enabled_methods.length > 0
              ? verifyData.enabled_methods
              : ['webauthn', 'inapp_approval', 'totp', 'email_otp', 'whatsapp_otp']
          };

          document.cookie = `mfa_pending_context=${encodeURIComponent(JSON.stringify(mfaContext))}; path=/; max-age=900; SameSite=Lax`;

          // Redirect to MFA page
          setTimeout(() => {
            router.push('/auth/mfa');
          }, 100);
          return;
        }

        console.log('[LOGIN] Discoverable passkey authentication successful!', {
          hasSessionToken: !!verifyData.session_token,
          hasUser: !!verifyData.user,
          userId: verifyData.user?.id
        });

        setSuccessMessage('Passkey authentication successful! Redirecting...');

        if (verifyData.session_token && verifyData.user) {
          // Save discovered user to remembered users list
          if (verifyData.user.email) {
            setEmail(verifyData.user.email);
            saveCredentials({
              userId: parseOptionalNumber(verifyData.user.id),
              displayName: verifyData.user.first_name
                ? `${verifyData.user.first_name} ${verifyData.user.last_name || ''}`.trim()
                : undefined,
              avatarUrl: verifyData.user.avatar_url,
              hasPin: verifyData.user?.has_pin
            });
          }

          await finishPasskeyLogin(verifyData);
        } else {
          console.error('[LOGIN] Missing session_token or user data:', {
            hasToken: !!verifyData.session_token,
            hasUser: !!verifyData.user
          });
        }
      }
    } catch (err: unknown) {
      console.error('[LOGIN] Discoverable passkey error:', err);

      // Handle specific error messages and types
      const errorMessage = getErrorMessage(err);
      const errorName = getErrorName(err);

      if (errorMessage.includes('Passkey not found')) {
        setLocalError('No passkey found on this device. Please sign in with your email and password.');
      } else if (errorMessage.includes('Passkey verification failed')) {
        setLocalError('Passkey verification failed. This could mean an incorrect PIN or password was entered. Please try again.');
      } else if (errorName === 'NotAllowedError') {
        // NotAllowedError can mean several things:
        // 1. User cancelled the operation
        // 2. Authentication timed out
        // 3. Incorrect PIN/password entered (most common on Windows Hello)
        setLocalError('Authentication failed. This could be due to an incorrect PIN/password, timeout, or cancellation. Please try again or sign in with your email and password.');
      } else if (errorName === 'InvalidStateError') {
        setLocalError('Invalid authentication state. Please refresh the page and try again.');
      } else if (errorName === 'AbortError') {
        setLocalError('Passkey authentication was cancelled. Please try again.');
      } else if (errorName === 'NotSupportedError') {
        setLocalError('Your browser or device does not support passkeys. Please sign in with your email and password.');
      } else if (errorName === 'SecurityError') {
        setLocalError('Security error during passkey authentication. Please ensure you are on a secure connection (HTTPS) and try again.');
      } else if (errorName === 'UnknownError') {
        setLocalError('An unknown error occurred during passkey authentication. Please sign in with your email and password.');
      } else {
        setLocalError('Passkey authentication failed. Please try again or sign in with your email and password.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e?: FormEvent, faceLoginToken?: string, canonicalContact?: string) => {
    e?.preventDefault();

    if (submitInFlightRef.current) {
      return;
    }

    const pin = pinCode.join('');

    // Password managers can fill the DOM input without an input event React
    // observes (e.g. when the field mounts after page load) — fall back to the
    // live DOM value so autofilled credentials still submit instead of
    // silently no-op'ing on an empty state value.
    const effectivePassword = password || passwordInputRef.current?.value || '';

    if ((!email && !canonicalContact) || (!faceLoginToken && (loginMethod === 'pin' ? !pin : !effectivePassword))) {
      return;
    }

    if (loginMethod === 'pin' && !/^\d{6}$/.test(pin) && !faceLoginToken) {
      setLocalError('Enter your 6-digit PIN to continue');
      return;
    }

    // While the entered identifier is locked, no further attempts are allowed
    if (isLockedOut) {
      return;
    }

    // Normalize phone number if user entered a phone instead of email.
    // Face login passes the canonical contact returned by face-login (the
    // token is already bound to that user server-side).
    const normalizedLogin = canonicalContact || normalizePhoneNumber(email);

    submitInFlightRef.current = true;

    try {

      // Whitelabel business email enforcement — block disallowed domains instantly
      if (isWhitelabel && enforceBusinessEmail && allowedEmailDomains.length) {
        if (normalizedLogin.includes('@') && !isEmailDomainAllowed(normalizedLogin, allowedEmailDomains)) {
          setLocalError('Please use your business email address to sign in to this portal');
          return;
        }
      }

      // Face login: the modal already verified the user and closed — show the
      // branded overlay while the token redeems so the wait isn't silent.
      // Success paths keep it (route loading persists through navigation);
      // the catch below clears it on any failure.
      if (faceLoginToken) {
        startRouteLoading('Face verified — signing you in', 'Finishing your secure sign-in.');
      }

      console.log('[LOGIN] Starting login attempt for:', email, '-> normalized:', normalizedLogin)

      const response = await login(
        faceLoginToken
          ? {
            login: normalizedLogin,
            face_login_token: faceLoginToken,
            auth_method: 'face',
          }
          : loginMethod === 'pin'
            ? {
              login: normalizedLogin,
              pin,
              auth_method: 'pin',
            }
            : {
              login: normalizedLogin,
              password: effectivePassword,
              auth_method: 'password',
              captcha_token: getCaptchaPassToken() || undefined,
            }
      );

      const rememberedUserInfo = {
        userId: response.user?.id,
        displayName: response.user?.first_name
          ? `${response.user.first_name} ${response.user.last_name || ''}`.trim()
          : undefined,
        avatarUrl: response.user?.avatar_url,
        hasPin: response.user?.has_pin ?? response.has_pin
      };

      console.log('[LOGIN] Login response:', {
        success: response.success,
        requires_verification: response.requires_verification,
        requires_mfa: response.requires_mfa,
        requires_device_verification: response.requires_device_verification,
      })

      if (response.success) {
        // Clear PIN and CAPTCHA failure counts on successful login
        if (email) {
          savePinFailureCount(email, 0);
          setPinFailureCount(0);
        }
        authGate.clearFailures();
        clearLoginLockout(normalizedLogin);

        // Check if verification is required
        if (response.requires_verification) {
          saveCredentials({ hasPin: rememberedUserInfo.hasPin });
          console.log('[LOGIN] Account requires verification, redirecting...', response);

          // Show success message about OTP being sent
          if (response.otp_sent) {
            setSuccessMessage('Verification code sent to your email. Redirecting to verification page...');
          } else if (response.otp_error) {
            setSuccessMessage('Account verification required. Redirecting to verification page...');
          }

          // Redirect to verification page after a brief delay
          setTimeout(() => {
            if (response.redirect_to) {
              pushWithLoading(
                appendPostLoginRedirect(response.redirect_to, redirectParam),
                'Preparing account verification...',
                'Opening the next sign-in step for you.'
              );
            } else {
              pushWithLoading(
                appendPostLoginRedirect(
                  `/auth/verify-account?userId=${response.user_id}&email=${encodeURIComponent(response.email || '')}&step=email`,
                  redirectParam,
                ),
                'Preparing account verification...',
                'Opening the next sign-in step for you.'
              );
            }
          }, 2000);

          return;
        }

        if (response.requires_device_verification) {
          saveCredentials({ hasPin: rememberedUserInfo.hasPin });
          const verificationSessionToken = response.verification_session_token || response.session_token

          if (verificationSessionToken) {
            const verificationContext = {
              deviceInfo: response.device_verification?.device_info || {},
              preferredMethod: response.device_verification?.preferred_method || 'email',
              availableMethods: response.device_verification?.available_methods || ['email'],
              autoSent: response.device_verification?.auto_sent || false,
              autoSentMessage: response.device_verification?.auto_sent_message || '',
            }

            localStorage.setItem('device_verification_pending', encodeURIComponent(JSON.stringify(verificationContext)))
            localStorage.setItem('oauth_pending_session_token', verificationSessionToken)
            document.cookie = `device_verification_pending=${encodeURIComponent(JSON.stringify(verificationContext))}; path=/; max-age=${60 * 10}`
            document.cookie = `oauth_pending_session_token=${verificationSessionToken}; path=/; max-age=${60 * 10}`
            document.cookie = `session_token=${verificationSessionToken}; path=/; max-age=${60 * 10}`
          }

          setSuccessMessage(response.device_verification?.auto_sent_message || 'We sent a verification code to confirm this new device. Redirecting...')

          setTimeout(() => {
            pushWithLoading(
              appendPostLoginRedirect('/auth/device-verification', redirectParam),
              'Checking your new device...',
              'Opening device verification securely.'
            )
          }, 1500)

          return
        }

        // Check if MFA is required
        if (response.requires_mfa) {
          saveCredentials({ hasPin: rememberedUserInfo.hasPin });
          console.log('[LOGIN] Account requires MFA, redirecting...', response);

          // Set MFA context in cookies for the MFA page
          if (response.enabled_methods && response.session_token) {
            document.cookie = `mfa_pending_context=${encodeURIComponent(JSON.stringify({
              userId: response.user_id, // Add userId for passkey MFA
              mfaMethods: response.enabled_methods,
              sessionToken: response.session_token
            }))}; path=/; max-age=${60 * 5}`; // 5 minutes

            document.cookie = `session_token=${response.session_token}; path=/; max-age=${60 * 5}`;
          }

          setSuccessMessage('Multi-factor authentication required. Redirecting...');

          // Redirect to MFA page after a brief delay
          setTimeout(() => {
            pushWithLoading(
              appendPostLoginRedirect('/auth/mfa', redirectParam),
              'Preparing multi-factor sign-in...',
              'Opening your security check now.'
            );
          }, 2000);

          return;
        }

        // Normal successful login
        console.log('[LOGIN] Login successful, checking onboarding status')

        // Save credentials with user info if available from response
        saveCredentials(rememberedUserInfo);

        // Check if owner needs to complete onboarding before going to dashboard
        try {
        } catch (onboardingError) {
          // Onboarding check removed - welcome redirects disabled
        }

        console.log('[LOGIN] Redirecting to client-zone')
        void primeOfflinePortalShell();

        // Use router.replace for client-side navigation (no full page reload)
        // This preserves React state including active calls on Capacitor
        // For web, cookies need to be set before navigation (done by authService)
        try {
          replaceWithLoading(
            redirectTarget,
            buildWelcomeMessage(response.user?.first_name, response.user?.last_name, 'Signing you in...'),
            'Opening your workspace now.',
          )
        } catch (navError) {
          console.error('[LOGIN] Navigation error:', navError)
          // Fallback to window.location if router fails
          startRouteLoading(
            buildWelcomeMessage(response.user?.first_name, response.user?.last_name, 'Signing you in...'),
            'Opening your workspace now.',
          )
          window.location.href = redirectTarget
        }
      }
    } catch (err: unknown) {
      // Clear the face-login overlay (and any pre-nav route loading) so the
      // error + retry UI is reachable again.
      setRouteLoadingState(null);

      const errorDetails = getAuthErrorDetails(err);

      console.error('[LOGIN] Login error:', err)
      console.error('[LOGIN] Error details:', {
        message: errorDetails.message,
        data: errorDetails.data,
        status: errorDetails.status,
        stack: errorDetails.stack
      })

      // Check for rate limiting (429)
      const isRateLimited = errorDetails.data?.rate_limited || errorDetails.data?.error === 'rate_limit_exceeded';
      if (isRateLimited) {
        const retryAfter = errorDetails.data?.retryAfter || 60;
        const scope = errorDetails.data?.rate_limit_scope || 'ip';
        setRateLimitInfo({ retryAfter, scope });
        setRateLimitCountdown(retryAfter);
        setLocalError(null);
        return;
      }

      // Account lockout (423) — persist the lock so the form stays disabled
      // (with a live countdown) until it expires, even across reloads.
      if (errorDetails.data?.locked === true || errorDetails.status === 423) {
        const parsedUntil = errorDetails.data?.locked_until ? Date.parse(errorDetails.data.locked_until) : NaN;
        const lockedUntilMs = Number.isFinite(parsedUntil)
          ? parsedUntil
          : Date.now() + (errorDetails.data?.remaining_minutes ?? 15) * 60 * 1000;
        saveLoginLockout(normalizedLogin, lockedUntilMs);
        setLockoutUntil(lockedUntilMs);
        setShowResetHint(true);
        setLocalError(errorDetails.data?.error || errorDetails.message || 'Account temporarily locked.');
        return;
      }

      // Invalid credentials — offer the password reset shortcut
      const backendError = errorDetails.data?.error || '';
      const credentialFailure =
        errorDetails.status === 401 ||
        /invalid credentials|incorrect email\/phone/i.test(backendError) ||
        /incorrect email\/phone/i.test(errorDetails.message || '');
      if (credentialFailure) {
        setShowResetHint(true);
      }

      // Track PIN failure count and lock out after 3 failed PIN attempts
      if (loginMethod === 'pin' && email) {
        const nextCount = getPinFailureCount(email) + 1;
        savePinFailureCount(email, nextCount);
        setPinFailureCount(nextCount);
        if (nextCount >= 3) {
          setLoginMethod('password');
          setPinCode(['', '', '', '', '', '']);
          setLocalError('PIN login disabled after 3 failed attempts. Please use your password.');
          return;
        }
      }

      // Backend says captcha is required (e.g. user has failures from another session/browser)
      // Force the security gate open immediately rather than waiting for the local count
      if (errorDetails.data?.requires_captcha) {
        authGate.require();
      } else if (loginMethod === 'password') {
        // Track password login failures for gate triggering
        authGate.recordFailure();
      }

      // Display user-friendly error
      setLocalError(errorDetails.data?.error || errorDetails.message || 'Login failed. Please try again.')
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleInputChange = () => {
    if (error) {
      clearError();
      setLocalError(null);
    }
    if (successMessage) {
      setSuccessMessage('');
    }
    setShowResetHint(false);
  };

  // ── Phone-approved portal sign-in ────────────────────────────────────
  // The browser creates a pending approval (face-portal-challenge POST),
  // shows "check your phone" + a QR fallback, then polls the nonce until
  // the phone approves a face match. On approval the poll returns a
  // single-use face_login token which rides the normal login pipeline.

  const cancelPhoneApproval = useCallback(() => {
    if (phoneApprovalPollRef.current) {
      clearTimeout(phoneApprovalPollRef.current);
      phoneApprovalPollRef.current = null;
    }
    setPhoneApproval(null);
    setPhoneApprovalPending(false);
  }, []);

  const startPhoneApproval = useCallback(async () => {
    if (phoneApprovalPending || phoneApproval) return;
    setPhoneApprovalError(null);
    setLocalError(null);
    setPhoneApprovalPending(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/auth/face-portal-challenge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact: normalizedIdentifier }),
      });
      const data = await response.json().catch(() => null) as {
        success?: boolean;
        face_available?: boolean;
        nonce?: string;
        qr_link?: string;
        deep_link?: string;
        expires_in?: number;
        error?: string;
      } | null;

      if (response.status === 429) {
        setPhoneApprovalError(data?.error || 'Too many attempts. Please try again later.');
        return;
      }
      if (!response.ok || !data?.success) {
        setPhoneApprovalError(data?.error || 'Could not start phone sign-in. Please try again.');
        return;
      }
      if (!data.face_available || !data.nonce) {
        setPhoneApprovalError(data.error || 'Phone sign-in is not available for this account.');
        return;
      }

      setPhoneApproval({
        nonce: data.nonce,
        qrLink: data.qr_link || data.deep_link || '',
        expiresAt: Date.now() + (data.expires_in ?? 120) * 1000,
      });
    } catch {
      setPhoneApprovalError('Could not start phone sign-in. Check your connection and try again.');
    } finally {
      setPhoneApprovalPending(false);
    }
  }, [normalizedIdentifier, phoneApproval, phoneApprovalPending]);

  // Poll the pending approval until the phone decides. The poll effect
  // is keyed on the nonce so restarts/cancels re-arm cleanly.
  useEffect(() => {
    if (!phoneApproval) return;
    const { nonce, expiresAt } = phoneApproval;
    let cancelled = false;

    const scheduleNext = (ms: number) => {
      if (cancelled) return;
      phoneApprovalPollRef.current = setTimeout(tick, ms);
    };

    const finish = (errorMessage?: string) => {
      if (cancelled) return;
      cancelled = true;
      setPhoneApproval(null);
      if (errorMessage) setPhoneApprovalError(errorMessage);
    };

    async function tick() {
      if (cancelled) return;
      if (Date.now() >= expiresAt) {
        finish('The approval request expired — tap "Use your phone" to send a new one.');
        return;
      }
      try {
        const response = await fetch(
          buildApiUrl(`/api/v1/auth/face-portal-challenge?n=${encodeURIComponent(nonce)}`)
        );
        const data = await response.json().catch(() => null) as {
          success?: boolean;
          status?: 'pending' | 'approved' | 'denied' | 'expired';
          login_token?: string;
          contact?: string;
        } | null;
        if (cancelled) return;

        if (!response.ok || !data?.success) {
          // Transient server error — keep polling within the TTL window.
          scheduleNext(2500);
          return;
        }
        if (data.status === 'approved' && data.login_token) {
          cancelled = true;
          setPhoneApproval(null);
          setPhoneApprovalError(null);
          void handleSubmit(undefined, data.login_token, data.contact || undefined);
          return;
        }
        if (data.status === 'denied') {
          finish('Sign-in was declined on your phone.');
          return;
        }
        if (data.status === 'expired') {
          finish('The approval request expired — tap "Use your phone" to send a new one.');
          return;
        }
        scheduleNext(2000);
      } catch {
        // Network blip — retry while the nonce is still alive.
        scheduleNext(2500);
      }
    }

    void tick();
    return () => {
      cancelled = true;
      if (phoneApprovalPollRef.current) {
        clearTimeout(phoneApprovalPollRef.current);
        phoneApprovalPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneApproval?.nonce]);

  // A new approval for a different account, a lockout mid-wait, or the user
  // switching back to credential entry should tear down any stale request.
  useEffect(() => {
    if (phoneApproval && (isLockedOut || isIdentifierInputVisible)) {
      cancelPhoneApproval();
    }
  }, [phoneApproval, isLockedOut, isIdentifierInputVisible, cancelPhoneApproval]);

  const queuePinAutoSubmit = useCallback((digits: string[]) => {
    if (loginMethod !== 'pin' || !supportsPinLogin) {
      return;
    }

    const pin = digits.join('');
    if (pin.length !== 6 || digits.some((digit) => !digit)) {
      return;
    }

    if (actionIsBusy) {
      return;
    }

    const submissionKey = pin;
    if (lastAutoSubmittedPinRef.current === submissionKey) {
      return;
    }

    lastAutoSubmittedPinRef.current = submissionKey;

    window.setTimeout(() => {
      loginFormRef.current?.requestSubmit();
    }, 0);
  }, [actionIsBusy, loginMethod, supportsPinLogin]);

  useEffect(() => {
    if (loginMethod !== 'pin') {
      return;
    }

    queuePinAutoSubmit(pinCode);
  }, [loginMethod, pinCode, queuePinAutoSubmit]);

  const handlePinDigitChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, '');
    const newCode = [...pinCode];

    if (!sanitizedValue) {
      newCode[index] = '';
      setPinCode(newCode);
      handleInputChange();
      return;
    }

    if (sanitizedValue.length > 1) {
      const nextDigits = sanitizedValue.slice(0, 6 - index).split('');
      nextDigits.forEach((digit, digitOffset) => {
        newCode[index + digitOffset] = digit;
      });

      setPinCode(newCode);
      handleInputChange();

      const nextIndex = Math.min(index + nextDigits.length, 5);
      window.setTimeout(() => {
        pinInputRefs.current[nextIndex]?.focus();
      }, 0);
      return;
    }

    newCode[index] = sanitizedValue;
    setPinCode(newCode);
    handleInputChange();

    if (index < 5) {
      window.setTimeout(() => {
        pinInputRefs.current[index + 1]?.focus();
      }, 0);
    }
  };

  const handlePinKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' || pinCode[index]) {
      return;
    }

    if (index > 0) {
      const newCode = [...pinCode];
      newCode[index - 1] = '';
      setPinCode(newCode);
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedPin = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedPin) {
      return;
    }

    const newCode = [...pinCode];
    pastedPin.split('').forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });

    setPinCode(newCode);
    handleInputChange();

    const focusIndex = Math.min(pastedPin.length - 1, 5);
    window.setTimeout(() => {
      pinInputRefs.current[focusIndex]?.focus();
    }, 0);
  };

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    setPinCode(['', '', '', '', '', '']);
    setLoginMethod('password');
    handleInputChange();
  };

  // Fetch SSO providers when accordion is expanded
  // On whitelabel domains, client is resolved from the domain automatically.
  // On the main domain, we need the email to resolve the user's default client.
  useEffect(() => {
    if (!ssoExpanded) return;

    let cancelled = false;

    const fetchSsoProviders = async () => {
      setSsoLoading(true);
      try {
        const params = new URLSearchParams();
        if (email.trim()) {
          params.set('email', email.trim());
        }
        const url = `/api/v1/auth/sso/initiate${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.providers)) {
          setSsoProviders(data.providers);
          if (data.client_id) setSsoClientId(data.client_id);
        }
      } catch {
        // silently fail — accordion just won't show providers
      } finally {
        if (!cancelled) setSsoLoading(false);
      }
    };

    void fetchSsoProviders();

    return () => { cancelled = true; };
  }, [ssoExpanded, email]);

  const handleSsoProviderClick = async (provider: typeof ssoProviders[number]) => {
    if (provider.protocol === 'saml' || provider.protocol === 'oidc') {
      if (provider.login_url) {
        const redirect = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';
        const fullUrl = `${provider.login_url}${redirect}`;

        if (isCapacitorNativeApp) {
          // On Capacitor, relative URLs won't work with window.location.href.
          // Use absolute URL and open in system browser (same as Google/Microsoft OAuth).
          const absoluteUrl = fullUrl.startsWith('http')
            ? fullUrl
            : buildApiUrl(fullUrl);
          try {
            await Browser.open({ url: absoluteUrl });
          } catch (err) {
            console.error('[LOGIN] Failed to open SSO URL in browser:', err);
            window.location.href = absoluteUrl;
          }
        } else {
          window.location.href = fullUrl;
        }
      }
      return;
    }
    // LDAP — redirect to the dedicated directory login page (OAuth-style full screen)
    if (provider.protocol === 'ldap') {
      const params = new URLSearchParams();
      // Use resolved client_id from email/whitelabel, or fall back to the provider's config
      const clientId = ssoClientId || (provider.id ? 1 : 1);
      params.set('client_id', String(clientId));
      if (redirectParam) params.set('redirect', redirectParam);
      const directoryUrl = `/auth/total-access-directory?${params.toString()}`;

      if (isCapacitorNativeApp) {
        const absoluteUrl = buildApiUrl(directoryUrl);
        try {
          await Browser.open({ url: absoluteUrl });
        } catch (err) {
          console.error('[LOGIN] Failed to open LDAP directory URL in browser:', err);
          window.location.href = absoluteUrl;
        }
      } else {
        pushWithLoading(directoryUrl, 'Opening directory login...', 'Connecting to the Total Access directory.');
      }
    }
  };

  // SSO button visibility — for returning users with an SSO profile, only
  // show buttons for providers they have linked. For new users (no profile),
  // show all buttons. The Total Access Directory (LDAP) button is shown only
  // if the user's business_email matches and enforce_business_email is true.
  const showGoogleSso = !ssoProfile || Boolean(ssoProfile.google_id);
  const showMicrosoftSso = !ssoProfile || Boolean(ssoProfile.microsoft_id);
  const showIntuitSso = !ssoProfile || Boolean(ssoProfile.intuit_id);
  const showXeroSso = !ssoProfile || Boolean(ssoProfile.xero_id);

  // Provider-specific login_hint emails — these may differ from the user's
  // TotalAccess email. e.g. gavin@totalaccess.co.za may have a Google account
  // gav@totalaccess.co.za. We pass the provider-specific email so the OAuth
  // provider can pre-select the correct account.
  const googleLoginHint = ssoProfile?.google_email || undefined;
  const microsoftLoginHint = ssoProfile?.microsoft_email || undefined;
  const intuitLoginHint = ssoProfile?.intuit_email || undefined;
  const xeroLoginHint = ssoProfile?.xero_email || undefined;
  // Total Access Directory: show if no profile (new user) or if business_email
  // is set and enforce_business_email is true for this user's client.
  const showTotalAccessSso = !ssoProfile || (Boolean(ssoProfile.business_email) && ssoProfile.enforce_business_email);
  // Hide the entire SSO section if a returning user has no linked providers
  // and no Total Access Directory eligibility.
  const hasAnySsoOption = showGoogleSso || showMicrosoftSso || showIntuitSso || showXeroSso || showTotalAccessSso || ssoProviders.length > 0;

  const handleGoogleSignIn = () => {
    try {
      if (isCapacitorNativeApp) {
        void capacitorOAuthHandler.initiateOAuth('google', redirectParam || undefined);
        return;
      }

      // Skip the Google account picker for returning users who have Google
      // linked, even if we don't have their google_email yet. Google will
      // use the active session instead.
      const skipSelection = Boolean(ssoProfile?.google_id);
      initiateOAuthFlow('google', redirectParam || undefined, googleLoginHint, skipSelection);
    } catch (error) {
      console.error('OAuth flow failed:', error);
    }
  };

  const handleMicrosoftSignIn = () => {
    try {
      if (isCapacitorNativeApp) {
        void capacitorOAuthHandler.initiateOAuth('microsoft', redirectParam || undefined);
        return;
      }

      initiateOAuthFlow('microsoft', redirectParam || undefined, microsoftLoginHint);
    } catch (error) {
      console.error('OAuth flow failed:', error);
    }
  };

  const handleIntuitSignIn = () => {
    try {
      // Intuit (QuickBooks) OAuth — web only, no Capacitor deep link support
      initiateOAuthFlow('intuit', redirectParam || undefined, intuitLoginHint);
    } catch (error) {
      console.error('Intuit OAuth flow failed:', error);
    }
  };

  const handleXeroSignIn = () => {
    try {
      if (isCapacitorNativeApp) {
        void capacitorOAuthHandler.initiateOAuth('xero', redirectParam || undefined);
        return;
      }

      initiateOAuthFlow('xero', redirectParam || undefined, xeroLoginHint);
    } catch (error) {
      console.error('Xero OAuth flow failed:', error);
    }
  };

  const hasHeroBackdrop = Boolean(authBackgroundUrl);
  const shouldUseLocalPageBackground = isCapacitorNativeApp || isDesktopApp || !isDesktopViewport;

  const backdropStyle = authBackgroundUrl
    ? ({
      backgroundImage: `url(${authBackgroundUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
    } as React.CSSProperties)
    : undefined;

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${isCapacitorNativeApp ? '' : 'px-3 py-4 sm:p-4'} relative ${shouldUseLocalPageBackground && !hasHeroBackdrop ? 'pm-gradient-hero' : ''}`}
      style={{
        ...(isCapacitorNativeApp ? { position: 'fixed', inset: 0, paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' } : {}),
        ...backdropStyle,
      }}
    >
      {!showAppLoadingOverlay && (
        <div className={`pm-card relative ${isCapacitorNativeApp ? 'p-4 w-full h-full overflow-y-auto' : 'max-w-md w-full p-4 sm:p-6 md:p-8'} ${shouldUseLocalPageBackground && hasHeroBackdrop ? 'z-10' : ''}`}>
          {/* Theme Toggle - top right corner (only for Capacitor since header is hidden) */}
          {isCapacitorNativeApp && (
            <div className="absolute top-3 right-3 z-10" style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}>
              <ThemeToggle />
            </div>
          )}

          <div className="text-center mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-teal-500 to-blue-600 w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
              <Lock className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
            </div>
            <h1 className="pm-auth-heading mb-2">Sign In</h1>
          </div>

          {/* Rate Limit Notice */}
          {rateLimitInfo && rateLimitCountdown > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
                    Rate Limit Active
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                    Too many login attempts from {rateLimitInfo.scope === 'browser' ? 'this browser' : 'this network'}.
                    Please wait for the cooldown to finish before trying again.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-300 tabular-nums">
                      {rateLimitCountdown}s
                    </div>
                    <div className="flex-1 h-2 bg-amber-200 dark:bg-amber-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 dark:bg-amber-400 rounded-full transition-all duration-1000 ease-linear"
                        style={{ width: `${Math.max(0, (rateLimitCountdown / rateLimitInfo.retryAfter) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    This is a temporary security cooldown — your access will resume automatically.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Account Lockout — live countdown, credential fields disabled */}
          {isLockedOut && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 p-4">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                    Account temporarily locked
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mb-2">
                    Too many failed login attempts. You can try signing in again when the cooldown finishes.
                  </p>
                  <div className="text-2xl font-bold text-red-700 dark:text-red-300 tabular-nums mb-2">
                    Try again in {lockoutCountdownLabel}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Link
                      href="/auth/reset-password"
                      onClick={createLoadingLinkHandler(
                        '/auth/reset-password',
                        'Opening password reset...',
                        'Waking the secure recovery page for you.'
                      )}
                      className="text-xs font-medium text-red-700 underline hover:text-red-800 dark:text-red-300 dark:hover:text-red-200"
                    >
                      Forgot your password? Reset it
                    </Link>
                    {faceRecoveryOffered && (
                      <button
                        type="button"
                        onClick={() => { setFaceModalMode('recovery'); setShowFaceAuth(true); }}
                        className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                      >
                        <ScanFace className="h-6 w-6" />
                        Unlock with Face Scan
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TCA device sign-in status — explains why auto-login hasn't
              fired instead of silently showing the form. Wrong account is
              the big one: the user must switch Windows accounts. */}
          {tcaAutoLogin.phase === 'wrong_account' && tcaAutoLogin.managedUsernames.length > 0 && (
            <div className="pm-alert mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
              <div className="flex items-start gap-3">
                <Shield className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold">This device is managed by Total Connect</p>
                  <p className="mt-1">
                    You&apos;re signed in to Windows as <strong>{tcaAutoLogin.consoleUser || 'another account'}</strong>.
                    To sign in here, switch to your work account
                    {tcaAutoLogin.managedUsernames.length === 1
                      ? <> <strong>{tcaAutoLogin.managedUsernames[0]}</strong></>
                      : <> (<strong>{tcaAutoLogin.managedUsernames.join(', ')}</strong>)</>} on the
                    Windows sign-in screen — this page will sign you in automatically.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      void tcaSwitchUser();
                    }}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-amber-700"
                  >
                    Switch to work account
                  </button>
                </div>
              </div>
            </div>
          )}
          {tcaAutoLogin.phase === 'screen_locked' && (
            <div className="pm-alert mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 shrink-0" />
                <p>Unlock Windows to finish signing in — this happens automatically.</p>
              </div>
            </div>
          )}
          {tcaAutoLogin.phase === 'no_binding' && (
            <div className="pm-alert mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 shrink-0" />
                <p>
                  This device is managed by Total Connect, but the Windows account
                  {' '}<strong>{tcaAutoLogin.consoleUser || 'you are using'}</strong> isn&apos;t
                  linked to a portal user — sign in below.
                </p>
              </div>
            </div>
          )}
          {tcaAutoLogin.phase === 'failed' && tcaAutoLogin.error && (
            <div className="pm-alert mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 shrink-0" />
                <p>Device sign-in didn&apos;t complete ({tcaAutoLogin.error}). You can sign in manually below — we&apos;ll keep retrying in the background.</p>
              </div>
            </div>
          )}

          {/* Error Message — suppressed while the lockout banner is showing */}
          {error && !isLockedOut && (
            <div className="pm-alert pm-alert-error mb-4">
              {error}
              {showResetHint && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Link
                    href="/auth/reset-password"
                    onClick={createLoadingLinkHandler(
                      '/auth/reset-password',
                      'Opening password reset...',
                      'Waking the secure recovery page for you.'
                    )}
                    className="font-medium underline"
                  >
                    Forgot your password? Reset it
                  </Link>
                </div>
              )}
              {faceRecoveryOffered && (
                <button
                  type="button"
                  onClick={() => { setFaceModalMode('recovery'); setShowFaceAuth(true); }}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                >
                  <ScanFace className="h-6 w-6" />
                  Unlock with Face Scan
                </button>
              )}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="pm-alert pm-alert-success mb-4">
              {successMessage}
            </div>
          )}

          {/* Login Form */}
          <form ref={loginFormRef} onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {rememberedEmail && !showSwitchUser && !showUserPicker && (
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={rememberedEmail}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
              />
            )}

            {/* Multi-User Picker (Desktop/Kiosk Mode) */}
            {showUserPicker && rememberedUsers.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Select User
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {rememberedUsers.map((user) => (
                    <div
                      key={user.email}
                      onClick={() => handleSelectUser(user)}
                      className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:from-teal-50 hover:to-blue-50 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </div>
                        )}
                        <div>
                          {user.displayName && (
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName}</div>
                          )}
                          <div className={`${user.displayName ? 'text-xs text-gray-500 dark:text-gray-400' : 'text-sm font-medium text-gray-900 dark:text-white'}`}>
                            {user.email}
                          </div>
                          {(user.hasStoredPassword || user.hasPin) && (
                            <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                              <Key className="w-3 h-3" />
                              {user.hasPin
                                ? (user.hasStoredPassword ? 'PIN or saved password' : 'PIN login available')
                                : 'Saved password ready'}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleForgetUser(user.email, e)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-opacity p-1"
                        title="Forget this user"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => { setShowUserPicker(false); setShowSwitchUser(true); }}
                  className="mt-3 w-full py-2 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium flex items-center justify-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Sign in with different account
                </button>
              </div>
            )}

            {/* Remembered User Display (Single User Mode) */}
            {rememberedEmail && !showSwitchUser && !showUserPicker && (
              <div className="mb-4">
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activeRememberedUser?.avatarUrl ? (
                      <img
                        src={activeRememberedUser.avatarUrl}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">WELCOME BACK</div>
                      {activeRememberedUser?.displayName && (
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{activeRememberedUser.displayName}</div>
                      )}
                      <div className={`${activeRememberedUser?.displayName ? 'text-xs text-gray-500 dark:text-gray-400' : 'text-sm font-medium text-gray-900 dark:text-white'}`}>
                        {rememberedEmail}
                      </div>
                      {(activeRememberedUser?.hasPin || activeRememberedUser?.hasStoredPassword) && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {activeRememberedUser?.hasPin
                            ? (activeRememberedUser?.hasStoredPassword ? 'Use your saved password or 6-digit PIN.' : 'Use your 6-digit PIN for quick sign-in.')
                            : 'Saved password ready for quick sign-in.'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {rememberedUsers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setShowUserPicker(true)}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        title="Show all users"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSwitchUser}
                      className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
                    >
                      Switch User
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PIN blocked notice */}
            {rememberedEmail && !showSwitchUser && !showUserPicker && pinFailureCount >= 3 && (
              <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2">
                <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
                  PIN login disabled after 3 failed attempts. Use your password to sign in.
                </p>
              </div>
            )}

            {/* Email Field */}
            {((!rememberedEmail && !showUserPicker) || showSwitchUser) && (
              <LoginIdentifierInput
                value={email}
                onChange={handleEmailChange}
                onEnter={focusPassword}
                disabled={actionIsBusy}
              />
            )}

            {(supportsPinLogin || loginMethod === 'pin') && !isIdentifierInputVisible && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('password');
                    setLocalError(null);
                    setPinCode(['', '', '', '', '', '']);
                  }}
                  className={`${loginMethod === 'password' ? 'pm-btn-primary' : 'pm-btn-outline'} box-border h-7 px-2 !py-0 text-xs leading-none`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('pin');
                    setLocalError(null);
                  }}
                  className={`${loginMethod === 'pin' ? 'pm-btn-primary' : 'pm-btn-outline'} box-border h-7 px-2 !py-0 text-xs leading-none`}
                >
                  Use PIN
                </button>
              </div>
            )}

            {loginMethod === 'pin' ? (
              <div>
                <label className="pm-label text-center block mb-3">
                  Enter your 6-digit PIN
                </label>
                <div className="flex justify-center space-x-2">
                  {pinCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => { pinInputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handlePinKeyDown(index, e)}
                      onPaste={handlePinPaste}
                      // type=text + one-time-code keeps password managers from
                      // autofilling the saved password into PIN boxes; the
                      // data-* hints cover common extension managers.
                      autoComplete="one-time-code"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      pattern="[0-9]*"
                      disabled={actionIsBusy || isLockedOut}
                      style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
                      className={`w-12 h-12 text-center text-xl font-semibold border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white transition-colors ${error && pinCode.join('').length === 6
                        ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                        }`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="password" className="pm-label">
                  Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      handleInputChange();
                    }}
                    disabled={actionIsBusy || isLockedOut}
                    className="pm-input pr-10"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {/* Password requirement check — a password that can't meet the
                    creation rules can't be correct, so we say so before it
                    burns a login attempt */}
                {passwordValidation.message && password.length > 0 && !isLockedOut && (
                  <div className="mt-1 text-sm text-red-600">
                    {passwordValidation.message}
                  </div>
                )}
                {!passwordValidation.message && password.length > 0 && passwordValidation.isValid && !isLockedOut && (
                  <div className="mt-1 text-sm text-green-600">
                    ✓ Password meets requirements
                  </div>
                )}
              </div>
            )}

            {/* Remember Password Checkbox */}
            {isCapacitorNativeApp && loginMethod === 'password' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember-password"
                  checked={rememberPassword}
                  onChange={(e) => setRememberPassword(e.target.checked)}
                  className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="remember-password" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Remember my password
                </label>
              </div>
            )}

            {loginMethod === 'pin' ? (
              <p className="text-center text-xs pm-text-secondary">
                Your PIN signs you in automatically when the sixth digit is entered.
              </p>
            ) : (
              <div>
                {isLoading ? (
                  <InlineLoadingPill label="Signing in..." className="py-1" />
                ) : (
                  <button
                    type="submit"
                    className="pm-btn-primary w-full box-border h-7 px-2 !py-0 text-xs leading-none disabled:opacity-50"
                    disabled={actionIsBusy || !identifierValid || !passwordMeetsRequirements || isLockedOut}
                  >
                    Sign In
                  </button>
                )}
              </div>
            )}
          </form>

          {/* Reset link for remembered users — the alternative-actions block
              below only renders for a fresh identifier entry, but a returning
              user is exactly who might be battling a forgotten password. */}
          {!isIdentifierInputVisible && (
            <div className="mt-3 text-center">
              <Link
                href="/auth/reset-password"
                onClick={createLoadingLinkHandler(
                  '/auth/reset-password',
                  'Opening password reset...',
                  'Waking the secure recovery page for you.'
                )}
                className="text-sm pm-text-secondary hover:text-teal-600 transition-colors"
              >
                Forgot your password? Reset it
              </Link>
            </div>
          )}

          {faceLoginOffered && (
            <button
              type="button"
              onClick={() => { setFaceModalMode('login'); setShowFaceAuth(true); }}
              disabled={actionIsBusy}
              className="pm-btn-outline mt-3 w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 disabled:opacity-50 text-xs leading-none"
            >
              <ScanFace className="w-4 h-4" />
              Sign in with your face
            </button>
          )}

          {/* Phone-approved sign-in (welcome-back users in a browser) — the
              server pushes to the user's enrolled installs; the QR encodes the
              same approval deep link as a fallback for killed/quiet FCM. */}
          {phoneApprovalOffered && !phoneApproval && (
            <button
              type="button"
              onClick={() => void startPhoneApproval()}
              disabled={actionIsBusy || phoneApprovalPending}
              className="pm-btn-outline mt-3 w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 disabled:opacity-50 text-xs leading-none"
            >
              <Smartphone className="w-4 h-4" />
              {phoneApprovalPending ? 'Contacting your phone…' : 'Approve on your phone'}
            </button>
          )}

          {phoneApproval && (
            <div className="mt-3 rounded-2xl border border-teal-200 bg-teal-50/80 p-4 text-center shadow-sm dark:border-teal-900/60 dark:bg-teal-950/20">
              <p className="text-xs font-semibold text-slate-900 dark:text-white">
                Check your phone
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                Approve this sign-in with your face on your enrolled device, or scan the code with the app.
              </p>
              {phoneApproval.qrLink && (
                <div className="mt-3 inline-block rounded-xl bg-white p-3">
                  <QRCodeSVG value={phoneApproval.qrLink} size={140} />
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                Request expires {new Date(phoneApproval.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={cancelPhoneApproval}
                  className="pm-btn-outline box-border h-7 px-3 !py-0 text-xs leading-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { cancelPhoneApproval(); void startPhoneApproval(); }}
                  className="text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                >
                  Resend
                </button>
              </div>
            </div>
          )}

          {phoneApprovalError && !phoneApproval && (
            <p className="mt-2 text-center text-xs text-amber-700 dark:text-amber-400">
              {phoneApprovalError}
            </p>
          )}

          {showLoginInstallCard && (
            <div className="mt-4 rounded-2xl border border-cyan-200 bg-cyan-50/80 p-4 text-left shadow-sm dark:border-cyan-900/60 dark:bg-cyan-950/20">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-cyan-100 p-2 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200">
                  {loginInstallContext.mode === 'android-store' ? (
                    <Smartphone className="h-5 w-5" />
                  ) : loginInstallContext.mode === 'appgallery' ? (
                    <Smartphone className="h-5 w-5" />
                  ) : (
                    <Download className="h-5 w-5" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">
                    Install on this device
                  </p>
                  <h2 className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                    {loginInstallCardTitle}
                  </h2>
                  <p className="mt-2 text-xs leading-5 text-slate-700 dark:text-slate-300">
                    {loginInstallCardBody}
                  </p>

                  {isInstallRedirectArmed && loginInstallContext.mode !== 'android-store' && loginInstallContext.mode !== 'appgallery' && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Install flow ready after sign-in
                    </div>
                  )}

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                    {loginInstallContext.mode === 'android-store' ? (
                      <Link
                        href={PLAYSTORE_ROUTE}
                        className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-cyan-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open Google Play
                      </Link>
                    ) : loginInstallContext.mode === 'appgallery' ? (
                      <Link
                        href={APPGALLERY_ROUTE}
                        className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open AppGallery
                      </Link>
                    ) : (
                      <Link
                        href={installAfterLoginHref}
                        className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-cyan-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-800"
                      >
                        <Download className="h-4 w-4" />
                        {isInstallRedirectArmed ? 'Sign in to continue' : 'Sign in, then install'}
                      </Link>
                    )}

                    <Link
                      href={PUBLIC_PWA_INSTALL_GUIDE_URL}
                      className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-cyan-200 px-4 py-2 text-sm font-semibold text-cyan-800 transition hover:border-cyan-300 hover:bg-white/80 dark:border-cyan-900/60 dark:text-cyan-200 dark:hover:bg-cyan-950/30"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {loginInstallGuideLabel}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Passkeys (WebAuthn) - not supported in Capacitor mobile apps */}
          {!isCapacitorNativeApp && (
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={shouldUseCentralPasskeyLogin ? () => continuePasskeyOnCentralPortal('discoverable') : handleDiscoverablePasskeyLogin}
                  disabled={actionIsBusy}
                  className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 disabled:opacity-50 text-xs leading-none"
                  title={shouldUseCentralPasskeyLogin ? `Continue secure passkey sign-in on ${centralPasskeyHostLabel}` : 'Sign in with FIDO2/WebAuthn'}
                >
                  <Shield className="w-4 h-4" />
                  {passkeyLoading ? '...' : shouldUseCentralPasskeyLogin ? 'Shared Passkey' : 'FIDO2/WebAuthn'}
                </button>

                <button
                  type="button"
                  onClick={shouldUseCentralPasskeyLogin ? () => continuePasskeyOnCentralPortal('email') : handlePasskeySignIn}
                  disabled={!identifierValid || actionIsBusy}
                  className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 disabled:opacity-50 text-xs leading-none"
                  title={identifierValid
                    ? (shouldUseCentralPasskeyLogin ? `Continue passkey sign-in on ${centralPasskeyHostLabel}` : 'Sign in with a passkey for this account')
                    : 'Enter your email or phone number first to use a passkey'}
                >
                  <Key className="w-4 h-4" />
                  {passkeyLoading ? '...' : shouldUseCentralPasskeyLogin ? 'Passkey via Shared Portal' : 'Passkey'}
                </button>
              </div>
            </div>
          )}

          {/* Single Sign-On Accordion — hidden for returning users with no linked SSO providers */}
          {hasAnySsoOption && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 pm-text-secondary">Or continue with</span>
              </div>
            </div>

            <div className="mt-4">
              {/* Accordion header button — full-width tap target */}
              <button
                type="button"
                onClick={() => setSsoExpanded((prev) => !prev)}
                disabled={actionIsBusy}
                className="pm-btn-outline w-full box-border min-h-[44px] px-3 py-2.5 flex items-center justify-between disabled:opacity-50 text-sm"
              >
                {/* Google icon — left aligned */}
                <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {/* Centered text + chevron */}
                <span className="flex-1 flex items-center justify-center gap-1.5">
                  <span>Single Sign On</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${ssoExpanded ? 'rotate-180' : ''}`}
                  />
                </span>
                {/* Microsoft icon — right aligned */}
                <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z" />
                  <path fill="#00A4EF" d="M13 1h10v10H13z" />
                  <path fill="#7FBA00" d="M1 13h10v10H1z" />
                  <path fill="#FFB900" d="M13 13h10v10H13z" />
                </svg>
              </button>

              {/* Accordion content */}
              {ssoExpanded && (
                <div className="mt-3 space-y-2">
                  {ssoLoading ? (
                    <div className="text-center text-xs pm-text-secondary py-2">Loading providers...</div>
                  ) : null}

                  {/* OAuth providers — 2-column grid (filtered by SSO profile for returning users) */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Google — shown if user has google_id linked */}
                    {showGoogleSso && (
                    <button
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={actionIsBusy}
                      className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      <span className="ml-2">Google</span>
                    </button>
                    )}

                    {/* Microsoft — shown if user has microsoft_id linked */}
                    {showMicrosoftSso && (
                    <button
                      type="button"
                      onClick={handleMicrosoftSignIn}
                      disabled={actionIsBusy}
                      className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#F25022" d="M1 1h10v10H1z" />
                        <path fill="#00A4EF" d="M13 1h10v10H13z" />
                        <path fill="#7FBA00" d="M1 13h10v10H1z" />
                        <path fill="#FFB900" d="M13 13h10v10H13z" />
                      </svg>
                      <span className="ml-2">Microsoft</span>
                    </button>
                    )}

                    {/* Intuit / QuickBooks — shown if user has intuit_id linked */}
                    {showIntuitSso && (
                    <button
                      type="button"
                      onClick={handleIntuitSignIn}
                      disabled={actionIsBusy}
                      className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                    >
                      {/* Intuit / QuickBooks logo */}
                      <img src="/bank-logos/Intuit Quickbooks.svg" alt="QuickBooks" className="h-5 w-5 object-contain" />
                      <span className="ml-2 truncate">QuickBooks</span>
                    </button>
                    )}

                    {/* Xero — shown if user has xero_id linked */}
                    {showXeroSso && (
                    <button
                      type="button"
                      onClick={handleXeroSignIn}
                      disabled={actionIsBusy}
                      className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                    >
                      {/* Xero logo */}
                      <img src="/bank-logos/xero.svg" alt="Xero" className="h-5 w-5 object-contain" />
                      <span className="ml-2">Xero</span>
                    </button>
                    )}

                    {/* Total Access Directory (LDAP) — shown if enforce_business_email is true for the user's client */}
                    {showTotalAccessSso && (
                    <button
                      type="button"
                      onClick={() => handleSsoProviderClick({ id: 1, protocol: 'ldap', display_name: 'Total Access', login_url: null })}
                      disabled={actionIsBusy}
                      className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                    >
                      <img src="/total-access-icon.png" alt="Total Access" className="h-4 w-4 object-contain" />
                      <span className="ml-2">Total Access</span>
                    </button>
                    )}
                  </div>

                  {/* SSO providers (SAML / OIDC / LDAP) — 2-column grid */}
                  {(ssoProviders.length > 0) && (
                    <div className="grid grid-cols-2 gap-2">
                      {/* SAML providers from SSO config */}
                      {ssoProviders.filter((p) => p.protocol === 'saml').map((provider) => (
                        <button
                          key={`saml-${provider.id}`}
                          type="button"
                          onClick={() => handleSsoProviderClick(provider)}
                          disabled={actionIsBusy}
                          className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                        >
                          <Shield className="h-4 w-4" />
                          <span className="ml-2 truncate">{provider.display_name || 'SAML'}</span>
                        </button>
                      ))}

                      {/* OIDC providers from SSO config */}
                      {ssoProviders.filter((p) => p.protocol === 'oidc').map((provider) => (
                        <button
                          key={`oidc-${provider.id}`}
                          type="button"
                          onClick={() => handleSsoProviderClick(provider)}
                          disabled={actionIsBusy}
                          className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                        >
                          <Key className="h-4 w-4" />
                          <span className="ml-2 truncate">{provider.display_name || 'OIDC'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Alternative Actions — hidden for returning users until they click "Switch User" */}
          {isIdentifierInputVisible && (
          <div className="mt-6">
            {/* Desktop App (Tauri) and Mobile App (Capacitor) - No signup, only reset password */}
            {(isDesktopApp || isCapacitorNativeApp) ? (
              <div className="space-y-4">
                <Link
                  href="/auth/reset-password"
                  onClick={createLoadingLinkHandler(
                    '/auth/reset-password',
                    'Opening password reset...',
                    'Waking the secure recovery page for you.'
                  )}
                  className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center text-xs leading-none"
                >
                  Reset Password
                </Link>
                <p className="text-center text-sm pm-text-secondary">
                  Need an account? Contact your organization administrator for an invitation.
                </p>
                <div className="text-center">
                  <Link
                    href="/diagnostics"
                    onClick={createLoadingLinkHandler(
                      '/diagnostics',
                      'Opening diagnostics...',
                      'Loading system checks and connectivity details.'
                    )}
                    className="text-sm pm-text-secondary hover:text-teal-600 transition-colors"
                  >
                    System Diagnostics
                  </Link>
                </div>
              </div>
            ) : (
              /* Web Portal - Show full options */
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <Link
                    href="/auth/reset-password"
                    onClick={createLoadingLinkHandler(
                      '/auth/reset-password',
                      'Opening password reset...',
                      'Waking the secure recovery page for you.'
                    )}
                    className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center text-xs leading-none"
                  >
                    Reset Password
                  </Link>
                  {/* Show Sign Up only if: not whitelabel OR (whitelabel AND allow_signup enabled) */}
                  {(!isWhitelabel || (isWhitelabel && allowSignup)) && (
                    <Link
                      href={isWhitelabel ? '/auth/signup' : '/auth/signup-options'}
                      className="pm-btn-primary w-full box-border h-7 px-2 !py-0 flex items-center justify-center text-xs leading-none"
                    >
                      Sign Up
                    </Link>
                  )}
                </div>

                <div className="text-center mt-4">
                  <Link
                    href="/diagnostics"
                    onClick={createLoadingLinkHandler(
                      '/diagnostics',
                      'Opening diagnostics...',
                      'Loading system checks and connectivity details.'
                    )}
                    className="text-sm pm-text-secondary hover:text-teal-600 transition-colors"
                  >
                    System Diagnostics
                  </Link>
                </div>
              </>
            )}
          </div>
          )}
        </div>
      )}

      <LoadingOverlay
        isVisible={showAppLoadingOverlay}
        message={routeLoadingState?.message || (tcaAutoLoginInProgress ? 'Checking for TCA auto-login…' : 'Loading...')}
        subMessage={routeLoadingState?.subMessage}
      />

      <AuthGateModal
        open={authGate.open}
        status={authGate.status}
        step={authGate.step}
        stepNumber={authGate.stepNumber}
        error={authGate.error}
        retryAfter={authGate.retryAfter}
        onSubmit={authGate.submitAnswer}
        onRestart={authGate.restart}
      />

      {isCapacitorNativeApp && (
        <FaceAuthenticationModal
          isOpen={showFaceAuth}
          contact={normalizedIdentifier}
          mode={faceModalMode}
          onClose={() => setShowFaceAuth(false)}
          onLoginSuccess={(loginToken, canonicalContact) => {
            setShowFaceAuth(false);
            void handleSubmit(undefined, loginToken, canonicalContact);
          }}
          onSuccess={(mfaRequired) => {
            clearLoginLockout(normalizedIdentifier);
            setLockoutUntil(null);
            setLocalError(null);
            setShowResetHint(false);
            setPassword('');
            setSuccessMessage(mfaRequired
              ? 'Password updated and account unlocked. Sign in with your new password — you will be asked to complete multi-factor authentication.'
              : 'Password updated and account unlocked. Sign in with your new password.');
          }}
        />
      )}
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <SectionLoader
        size="lg"
        message="Loading sign-in"
        subMessage="Preparing your secure access experience."
        showTagline={true}
        className="min-h-screen w-full pm-gradient-hero px-4"
      />
    }>
      <LoginPageContent />
    </Suspense>
  )
}

declare global {
  interface Window {
    __taStartupScreenReadySignaled?: boolean;
  }
}
